import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  LinearProgress,
  DialogContent,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import { incompleteMessage, isIncomplete } from "bundles/Internal/utils/brandedCv";
import { GenerateCvSplitButton } from "bundles/Internal/components/common/BrandedCvMenu";
import { brandedErrorCopy, brandedStageDetail, brandedStageLabel, brandedStageProgress } from "bundles/Internal/components/cv_vault/BrandedCvStatusPanel";
import { errorCopy } from "bundles/Internal/components/cv_vault/ParseStatusPanel";
import BrandedCvEditor from "bundles/Internal/components/cv_vault/BrandedCvEditor";
import {
  ChevronDown,
  User,
  Briefcase,
  GraduationCap,
  MapPin,
  FileText,
  Search,
  Sparkles,
  Award,
  UserPlus,
  Pencil,
  Plus,
  Trash2,
  X,
  Upload,
  Wand2,
  Minimize2,
  AlertTriangle,
  Check,
  RotateCcw,
  Undo2,
  FileWarning,
} from "lucide-react";
import {
  be_regions,
  lu_regions,
  fr_regions,
  companyIndustryMap,
  companySubSectorMap,
  candidateStatus,
} from "bundles/common/utils/constants";
import { formatRegionMessage, humanizeRegion } from "bundles/common/utils/utils";
import { enqueueSnackbar } from "notistack";
import translations from "app/libs/i18n/translations.json";
import BelgiumFlag from "icons/belgium-flag.svg";
import FranceFlag from "icons/france-flag.svg";
import UkFlag from "icons/uk-flag.svg";
import { CircleUnchecked, CircleChecked } from "./CheckIcons";
import RichTextEditor from "./RichTextEditor";
import ConfirmDialog from "./ConfirmDialog";
import CvUpdateReviewModal from "./CvUpdateReviewModal";
import { DEFAULT_PARSER_MODEL, ParserModelSplitButton } from "./ParserModelMenu";
import CvViewer from "./CvViewer";
import consumer from "bundles/Internal/channels/consumer";

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  region: "",
  country: "",
  zipcode: "",
  city: "",
  job_title: "",
  desired_sector: [],
  subsectors: [],
  desired_job_title: "",
  desired_locations: "",
  portfolio_url: "",
  experience_year: "",
  preferred_locale: "",
  availability: "",
  candidate_status: "",
  highest_degree: "",
  presentation: "",
  ai_summary: "",
  ai_summary_locale: "",
  whitelisted: true,
  work_permit: true,
  source: "",
  source_other: "",
};

const AVAILABILITY_OPTIONS = ["available", "unavailable", "looking", "listening", "currently_employed"];
const HIGHEST_DEGREE_OPTIONS = ["no_degree", "highschool", "bachelor", "master", "phd", "other_training", "cap", "but", "bts", "deust", "bac_pro"];
const CANDIDATE_STATUS_OPTIONS = candidateStatus;
const COUNTRY_OPTIONS = ["belgium", "france", "germany", "luxembourg"];
const JOB_STATUS_OPTIONS = ["independent_type", "freelance", "fixedterm", "permanent", "trainee", "bachelor", "master", "training", "internship"];
const SOURCE_OPTIONS = [
  ["forem", "Forem"],
  ["vdab", "VDAB"],
  ["actiris", "Actiris"],
  ["france_travail", "France Travail"],
  ["stepstone", "StepStone"],
  ["apec", "APEC"],
  ["indeed", "Indeed"],
  ["linkedin", "LinkedIn"],
  ["other", "Other (specify below)"],
];
const DEFAULT_CANDIDATE_PASSWORD = "Squarehub12345!";

// Same four the public SelectLanguage offers, with the same labels and flags, so what the team sets
// here is exactly what the candidate would have picked for themselves.
const LANGUAGE_OPTIONS = [
  { value: "fr", label: "Français (BE)", flag: BelgiumFlag },
  { value: "fr-fr", label: "Français (FR)", flag: FranceFlag },
  { value: "en", label: "English", flag: UkFlag },
  { value: "nl", label: "Nederlands", flag: BelgiumFlag },
];

const SKILL_CATEGORIES = [
  { key: "hard", label: "Hard skills" },
  { key: "software", label: "Software" },
  { key: "soft", label: "Soft skills" },
  { key: "lang", label: "Languages" },
  { key: "licence", label: "Licences" },
];

// The skill taxonomy is generated into translations.json from config/locales/frontend/*.yml. The
// internal bundle is English-only, so it reads the `en` map directly instead of going through
// react-intl — same reason the copy in this file is hardcoded.
const SKILL_OPTIONS = SKILL_CATEGORIES.reduce((acc, { key }) => {
  const prefix = `skills_list.${key}.`;
  acc[key] = Object.entries(translations.en || {})
    .filter(([id]) => id.startsWith(prefix))
    .map(([id, label]) => ({ id: id.slice(prefix.length), label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return acc;
}, {});

const CERTIFICATE_SECTORS = ["construction", "architecture", "engineering", "industry"];

const CERTIFICATE_OPTIONS = CERTIFICATE_SECTORS.flatMap((sector) => {
  const prefix = `certificates_list.${sector}.`;
  const group = translations.en?.[`certificates_sector_label.${sector}`] || sector;
  return Object.entries(translations.en || {})
    .filter(([id]) => id.startsWith(prefix))
    .map(([id, label]) => ({ id: id.slice(prefix.length), label, category: sector, group }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

const splitCommaList = (s) => {
  if (!s) return [];
  return String(s).split(",").map((x) => x.trim()).filter(Boolean);
};

// --- Profile completion -----------------------------------------------------
// Mirrors Candidate::PROFILE_COMPLETION_MAPPING so the number the recruiter sees here is the one
// that will be stored. `profile_picture_url` (weight 10) is deliberately absent: this form cannot
// set it, so it is dead weight in the denominator and would only ever read as a permanent penalty.
const COMPLETION_WEIGHTS = [
  { key: "resume", weight: 30 },
  { key: "experiences", weight: 10 },
  { key: "trainings", weight: 10 },
  { key: "skills", weight: 8 },
  { key: "presentation", weight: 4 },
  { key: "availability", weight: 4 },
  { key: "desired_sector", weight: 4 },
  { key: "candidate_status", weight: 4 },
  { key: "first_name", weight: 2 },
  { key: "last_name", weight: 2 },
  { key: "email", weight: 2 },
  { key: "phone", weight: 2 },
  { key: "job_title", weight: 2 },
  { key: "experience_year", weight: 2 },
  { key: "region", weight: 2 },
  { key: "portfolio_url", weight: 2 },
];

const COMPLETION_TOTAL = COMPLETION_WEIGHTS.reduce((sum, item) => sum + item.weight, 0);

// What a drop or a paste is allowed to attach. Matches the file input's own `accept`.
const ACCEPTED_CV = /\.(pdf|docx?)$/i;

// Height of the sticky progress/nav bar, which a jumped-to section has to clear.
const STICKY_NAV_OFFSET = 84;

const FORM_SECTIONS = [
  { key: "basic", label: "Basics" },
  { key: "cv", label: "CV" },
  { key: "location", label: "Location" },
  { key: "professional", label: "Profile" },
  { key: "experience", label: "Experience" },
  { key: "training", label: "Training" },
  { key: "skills", label: "Skills" },
  { key: "certificates", label: "Certifications" },
];

// --- CV extraction ----------------------------------------------------------

const DEFAULT_CONFIDENCE_THRESHOLD = 0.85;

const num = (n) => Number(n).toLocaleString();

// Mirrors CvParsePreviewStore::STAGES, plus "uploading" for the POST the browser does itself.
const PARSE_STAGES = [
  { key: "uploading", label: "Uploading the file" },
  { key: "queued", label: "Waiting for a free slot" },
  { key: "reading", label: "Reading the document", detail: (d) => (d.chars ? `${num(d.chars)} characters` : null) },
  { key: "redacting", label: "Removing personal data", detail: (d) => (d.removed ? `${d.removed} identifier${d.removed === 1 ? "" : "s"} removed` : null) },
  { key: "extracting", label: "Reading the profile", detail: (d) => d.model || null },
  { key: "merging", label: "Assembling the profile", detail: (d) => (d.total ? `${d.done} of ${d.total} sections back` : null) },
  { key: "checking", label: "Checking against our lists" },
  { key: "done", label: "Done" },
];

const SPLIT_STORAGE_KEY = "cv-modal-split";
const DEFAULT_FORM_PCT = 50;
const MIN_PANE_PCT = 25;
const MAX_PANE_PCT = 75;

// Until the server answers with measured percentiles.
const DEFAULT_EXPECTED = { p50: 60, p90: 120 };

// Fields the parser fills that the form has an input for. Anything else on the payload has no
// home yet and is surfaced under "also found" instead of being dropped.
const PARSED_FIELDS = [
  "first_name", "last_name", "email", "phone", "region", "country", "city", "zipcode", "job_title",
  "experience_year", "preferred_locale", "candidate_status", "highest_degree",
  "presentation", "ai_summary",
];

const FIELD_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  phone: "Phone",
  region: "Location",
  city: "City",
  zipcode: "Zipcode",
  job_title: "Current job title",
  experience_year: "Years of experience",
  highest_degree: "Highest degree",
};

// Fields worth flagging when the CV disagrees with what's already filled in. Free text
// (presentation, ai_summary) and vocabulary-constrained fields are excluded.
const CONFLICT_FIELDS = ["first_name", "last_name", "email", "phone", "region", "city", "zipcode", "job_title", "experience_year", "highest_degree"];

const normalizeForCompare = (value) => (value ?? "").toString().trim().toLowerCase();

const formatExperienceDates = (start, end) => {
  if (!start && !end) return "";
  return `${start || "?"} → ${end || "Present"}`;
};

const EXTRA_LABELS = {
  certifications: "Certifications",
  bim_role: "BIM role",
  professional_memberships: "Memberships",
  notice_period: "Notice period",
  linkedin_url: "LinkedIn",
  willing_to_relocate: "Willing to relocate",
};

const formatSeconds = (total) => {
  const s = Math.max(0, Math.round(total));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
};

const humanizeExtra = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
};

const confidenceTone = (confidence, threshold) => {
  if (confidence == null) return { color: "hsl(150 38% 17%)", bg: "hsla(150, 38%, 17%, 0.08)" };
  return confidence >= threshold
    ? { color: "hsl(150 38% 17%)", bg: "hsla(150, 38%, 17%, 0.08)" }
    : { color: "hsl(35 92% 33%)", bg: "hsl(40 100% 94%)" };
};

// Shows the value the parser proposed and the evidence it recorded. There is deliberately no
// click-to-highlight in the CV: roughly half of these snippets are explanations the pipeline wrote
// ("matched on: email", "highest-ranked diploma on the CV") rather than quotes, and the quotes that
// do exist come from the PII-redacted copy of the text, so they often do not occur in the document.
const ProvenanceBadge = ({ info, threshold }) => {
  const [anchor, setAnchor] = useState(null);
  if (!info) return null;

  const tone = confidenceTone(info.confidence, threshold);
  const pct = info.confidence == null ? null : Math.round(info.confidence * 100);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => { e.preventDefault(); setAnchor(e.currentTarget); }}
        aria-label="Where this value came from"
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.25, ml: 0.5, px: 0.5,
          border: "none", borderRadius: "4px", cursor: "pointer", verticalAlign: "middle",
          backgroundColor: tone.bg, color: tone.color, lineHeight: 1,
        }}
      >
        <Sparkles size={9} />
        {pct != null && (
          <Box component="span" sx={{ fontSize: 9, fontWeight: 700 }}>{pct}%</Box>
        )}
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { p: 1.5, maxWidth: 340, borderRadius: "8px" } }}
      >
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5 }}>
          From the CV
        </Typography>
        <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
          {info.snippet || "The parser gave no evidence for this value."}
        </Typography>
        {pct != null && (
          <Typography sx={{ fontSize: 11, mt: 1, color: tone.color }}>
            {pct >= threshold * 100 ? "Confident" : "Low confidence, worth checking"} · {pct}%
          </Typography>
        )}
      </Popover>
    </>
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Rows carry a client-side key so React keeps input focus while a row above it is deleted; it is
// stripped before submit. Existing rows also carry the DB `id` so nested attributes update in place.
let rowKeySeed = 0;
const nextRowKey = () => {
  rowKeySeed += 1;
  return `row-${rowKeySeed}`;
};

const blankExperience = () => ({
  _key: nextRowKey(), id: undefined, title: "", company: "", location: "",
  description: "", start_date: "", end_date: "", job_status: "", still_active: false,
});

const blankTraining = () => ({
  _key: nextRowKey(), id: undefined, institution: "", diploma: "", study_field: "",
  location: "", honors: "", start_date: "", end_date: "",
});

const toExperienceRow = (exp) => ({
  ...blankExperience(),
  ...exp,
  still_active: Boolean(exp.start_date) && !exp.end_date,
  end_date: exp.end_date || "",
});

const toTrainingRow = (tr) => ({ ...blankTraining(), ...tr, end_date: tr.end_date || "" });

// Both dates are plain YYYY-MM-DD strings from <input type="date">, which sort lexicographically.
const isDateOrderInvalid = (row) => Boolean(row.start_date && row.end_date && row.end_date < row.start_date);

// Spelled out rather than spread so the render-only keys (`_key`, `still_active`) can never reach
// strong params, and so the column list here stays the one the controller permits.
const toExperienceAttributes = (row) => ({
  id: row.id,
  title: row.title,
  company: row.company,
  location: row.location,
  description: row.description,
  start_date: row.start_date,
  end_date: row.still_active ? "" : row.end_date,
  job_status: row.job_status,
});

const toTrainingAttributes = (row) => ({
  id: row.id,
  institution: row.institution,
  study_field: row.study_field,
  diploma: row.diploma,
  location: row.location,
  honors: row.honors,
  start_date: row.start_date,
  end_date: row.end_date,
});

const labelSx = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "hsl(150 10% 46%)",
  mb: 0.5,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
};

const nativeInputSx = {
  width: "100%",
  height: 40,
  borderRadius: "6px",
  border: "1px solid hsl(140 8% 88%)",
  backgroundColor: "hsl(140 10% 95%)",
  px: 1.5,
  py: 1,
  fontSize: 14,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  outline: "none",
  boxSizing: "border-box",
  "&::placeholder": { color: "hsl(150 10% 46%)" },
  "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
  "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
};

const invalidInputSx = {
  borderColor: "hsl(0 72% 51%)",
  backgroundColor: "hsl(0 80% 98%)",
  "&:focus": { outline: "2px solid hsl(0 72% 51%)", outlineOffset: "2px" },
};

const errorTextSx = {
  fontSize: 11,
  mt: 0.5,
  color: "hsl(0 72% 51%)",
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "6px",
    fontSize: 14,
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(150 30% 12%)",
    backgroundColor: "hsl(140 10% 95%)",
    "& fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const textareaSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    fontSize: 14,
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(150 30% 12%)",
    backgroundColor: "hsl(140 10% 95%)",
    "& fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const accordionSx = {
  borderRadius: "8px !important",
  border: "1px solid hsl(140 8% 88%)",
  "&:before": { display: "none" },
  overflow: "hidden",
};

const accordionSummarySx = {
  backgroundColor: "hsl(140 10% 95%)",
  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
};

const accordionTitleSx = {
  fontWeight: 600,
  color: "hsl(150 30% 12%)",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
};

// --- Field primitives -------------------------------------------------------
// Declared at module scope on purpose: defined inside CreateCandidateModal they would be a new
// component type on every render, so React would unmount the input on each keystroke and drop focus.

const FieldLabel = ({ label, required, provenance, threshold }) => (
  <Typography sx={labelSx}>
    {label}
    {required && <Box component="span" sx={{ color: "hsl(0 72% 51%)", ml: 0.25 }}>*</Box>}
    <ProvenanceBadge info={provenance} threshold={threshold} />
  </Typography>
);

const TextInput = ({ label, value, onChange, onBlur, required, error, type, placeholder, disabled, inputMode, provenance, threshold }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <FieldLabel label={label} required={required} provenance={provenance} threshold={threshold} />
    <Box
      component="input"
      type={type || "text"}
      inputMode={inputMode}
      placeholder={placeholder}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      sx={{ ...nativeInputSx, ...(error ? invalidInputSx : {}) }}
    />
    {error && <Typography sx={errorTextSx}>{error}</Typography>}
  </Box>
);

const LanguageSelect = ({ value, onChange, provenance, threshold }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <FieldLabel label="Preferred language" provenance={provenance} threshold={threshold} />
    <TextField select fullWidth size="small" value={value ?? ""} onChange={(e) => onChange(e.target.value)} sx={selectSx}>
      <MenuItem value="" sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>-</MenuItem>
      {LANGUAGE_OPTIONS.map(({ value: locale, label, flag }) => (
        <MenuItem key={locale} value={locale} sx={{ fontSize: 14, display: "flex", gap: 1 }}>
          <Box component="img" src={flag} alt="" sx={{ width: 16, height: 12, flexShrink: 0 }} />
          {label}
        </MenuItem>
      ))}
    </TextField>
  </Box>
);

const SelectInput = ({ label, value, onChange, options, required, placeholder = "-", renderOption = humanizeRegion, provenance, threshold }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <FieldLabel label={label} required={required} provenance={provenance} threshold={threshold} />
    <TextField select fullWidth size="small" value={value ?? ""} onChange={(e) => onChange(e.target.value)} sx={selectSx}>
      <MenuItem value="" sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>{placeholder}</MenuItem>
      {options.map((option) => {
        const [key, text] = Array.isArray(option) ? option : [option, renderOption(option)];
        return <MenuItem key={key} value={key} sx={{ fontSize: 14 }}>{text}</MenuItem>;
      })}
    </TextField>
  </Box>
);

const TextAreaInput = ({ label, value, onChange, rows = 3, placeholder, provenance, threshold }) => (
  <Box>
    <FieldLabel label={label} provenance={provenance} threshold={threshold} />
    <TextField
      fullWidth
      size="small"
      multiline
      minRows={rows}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      sx={textareaSx}
    />
  </Box>
);

const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 0.5 }}>
    <Box
      sx={{
        backgroundColor: "hsl(150 38% 17%)",
        borderRadius: "6px",
        p: 0.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon size={14} color="hsl(80 97% 72%)" />
    </Box>
    <Typography
      sx={{
        fontWeight: 600,
        color: "hsl(150 38% 17%)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      }}
    >
      {title}
    </Typography>
  </Box>
);

const RowCard = ({ index, title, onRemove, children }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, borderRadius: "8px", border: "1px solid hsl(140 8% 88%)", backgroundColor: "#fff" }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography sx={{ ...accordionTitleSx, fontSize: 11, color: "hsl(150 38% 17%)" }}>
        {title} #{index + 1}
      </Typography>
      <IconButton
        disableRipple
        size="small"
        onClick={onRemove}
        aria-label={`Remove ${title.toLowerCase()} ${index + 1}`}
        sx={{ p: 0.5, color: "hsl(0 72% 51%)", "&:hover": { backgroundColor: "hsl(0 80% 96%)" } }}
      >
        <Trash2 size={15} />
      </IconButton>
    </Stack>
    <Stack spacing={1.5}>{children}</Stack>
  </Paper>
);

const AddRowButton = ({ label, onClick }) => (
  <Button
    disableRipple
    startIcon={<Plus size={16} />}
    onClick={onClick}
    sx={{
      alignSelf: "flex-start",
      textTransform: "none",
      fontSize: 13,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      color: "hsl(150 38% 17%)",
      border: "1px dashed hsl(150 20% 60%)",
      borderRadius: "8px",
      px: 2,
      py: 0.75,
      "&:hover": { backgroundColor: "hsl(140 10% 95%)", borderColor: "hsl(150 38% 17%)" },
    }}
  >
    {label}
  </Button>
);

// Mirrors the candidate-facing EditExperienceModal field set, plus `job_status` — the CV parser
// fills that one and the talent team needs to be able to correct it.
const ExperienceRow = ({ row, index, onChange, onRemove, showErrors }) => {
  const [touched, setTouched] = useState({});
  const set = (key) => (value) => onChange(index, { ...row, [key]: value });
  const touch = (key) => () => setTouched((prev) => ({ ...prev, [key]: true }));
  const toggleActive = () => onChange(index, { ...row, still_active: !row.still_active, end_date: "" });
  const missing = (key) => ((showErrors || touched[key]) && !row[key] ? "Required" : undefined);

  return (
    <RowCard index={index} title="Experience" onRemove={() => onRemove(index)}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <TextInput label="Job title" required error={missing("title")} value={row.title} onChange={set("title")} onBlur={touch("title")} />
        <TextInput label="Company" required error={missing("company")} value={row.company} onChange={set("company")} onBlur={touch("company")} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <TextInput label="Location" value={row.location} onChange={set("location")} />
        <SelectInput label="Contract type" value={row.job_status} onChange={set("job_status")} options={JOB_STATUS_OPTIONS} />
      </Box>
      <FormControlLabel
        control={<Switch size="small" checked={row.still_active} onChange={toggleActive} />}
        label={<Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>Still working here</Typography>}
        sx={{ ml: 0 }}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <TextInput label="Start date" type="date" required error={missing("start_date")} value={row.start_date} onChange={set("start_date")} onBlur={touch("start_date")} />
        <TextInput
          label="End date"
          type="date"
          disabled={row.still_active}
          error={isDateOrderInvalid(row) ? "End date is before start date" : undefined}
          value={row.end_date}
          onChange={set("end_date")}
        />
      </Box>
      <TextAreaInput label="Description" rows={4} value={row.description} onChange={set("description")} />
    </RowCard>
  );
};

// Mirrors the candidate-facing EditTrainingModal field set.
const TrainingRow = ({ row, index, onChange, onRemove, showErrors }) => {
  const [touched, setTouched] = useState({});
  const set = (key) => (value) => onChange(index, { ...row, [key]: value });
  const touch = (key) => () => setTouched((prev) => ({ ...prev, [key]: true }));
  const missing = (key) => ((showErrors || touched[key]) && !row[key] ? "Required" : undefined);

  return (
    <RowCard index={index} title="Training" onRemove={() => onRemove(index)}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <TextInput label="Institution" value={row.institution} onChange={set("institution")} />
        <TextInput label="Diploma" required error={missing("diploma")} value={row.diploma} onChange={set("diploma")} onBlur={touch("diploma")} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <TextInput label="Field of study" value={row.study_field} onChange={set("study_field")} />
        <TextInput label="Location" value={row.location} onChange={set("location")} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <TextInput label="Start date" type="date" value={row.start_date} onChange={set("start_date")} />
        <TextInput
          label="End date"
          type="date"
          error={isDateOrderInvalid(row) ? "End date is before start date" : undefined}
          value={row.end_date}
          onChange={set("end_date")}
        />
      </Box>
      <TextInput label="Honors" value={row.honors} onChange={set("honors")} />
    </RowCard>
  );
};

const SkillPicker = ({ category, label, selected, onChange }) => {
  const options = SKILL_OPTIONS[category] || [];
  // A skill the taxonomy no longer lists (a company-custom row, or a renamed key) still has to
  // render, so fall back to the raw key as its own label rather than dropping it silently.
  const value = selected.map(
    (name) => options.find((o) => o.id === name) || { id: name, label: humanizeRegion(name) },
  );

  return (
    <Box>
      <FieldLabel label={label} />
      <Autocomplete
        multiple
        filterSelectedOptions
        size="small"
        options={options}
        value={value}
        onChange={(_e, next) => onChange(category, next.map((o) => o.id))}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        getOptionLabel={(option) => option.label}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => (
            <Chip
              {...getTagProps({ index })}
              key={tag.id}
              label={tag.label}
              size="small"
              sx={{
                fontSize: 12,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                backgroundColor: "hsl(150 38% 17%)",
                color: "hsl(80 97% 72%)",
                "& .MuiChip-deleteIcon": {
                  color: "hsla(80, 97%, 72%, 0.6)",
                  "&:hover": { color: "hsl(80 97% 72%)" },
                },
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} placeholder={value.length ? "" : "Type to search…"} sx={textareaSx} />
        )}
      />
    </Box>
  );
};

const CertificatePicker = ({ selected, onChange }) => {
  const value = selected.map(
    (c) => CERTIFICATE_OPTIONS.find((o) => o.id === c.name && o.category === c.category)
      || { id: c.name, label: c.name, category: c.category, group: c.category },
  );

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      size="small"
      options={CERTIFICATE_OPTIONS}
      groupBy={(option) => option.group}
      value={value}
      onChange={(_e, next) => onChange(next)}
      isOptionEqualToValue={(option, val) => option.id === val.id && option.category === val.category}
      getOptionLabel={(option) => option.label}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => (
          <Chip
            {...getTagProps({ index })}
            key={`${tag.category}-${tag.id}`}
            label={tag.label}
            size="small"
            sx={{
              fontSize: 12,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              backgroundColor: "hsla(40, 90%, 50%, 0.15)",
              color: "hsl(40, 90%, 30%)",
              "& .MuiChip-deleteIcon": {
                color: "hsla(40, 90%, 30%, 0.6)",
                "&:hover": { color: "hsl(40, 90%, 30%)" },
              },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} placeholder={value.length ? "" : "Search a certification…"} sx={textareaSx} />
      )}
    />
  );
};

const sectorChipSx = {
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  backgroundColor: "hsl(150 38% 17%)",
  color: "hsl(80 97% 72%)",
  "& .MuiChip-deleteIcon": {
    color: "hsla(80, 97%, 72%, 0.6)",
    "&:hover": { color: "hsl(80 97% 72%)" },
  },
};

const SectorPicker = ({ selected, onChange, provenance, threshold }) => {
  const options = companyIndustryMap
    .filter((key) => key !== "administration")
    .map((key) => ({ id: key, label: humanizeRegion(key) }));
  const value = selected.map((key) => options.find((o) => o.id === key) || { id: key, label: humanizeRegion(key) });

  return (
    <Box>
      <FieldLabel label="Sector / industry" provenance={provenance} threshold={threshold} />
      <Autocomplete
        multiple
        filterSelectedOptions
        size="small"
        options={options}
        value={value}
        onChange={(_e, next) => onChange(next.map((o) => o.id))}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        getOptionLabel={(option) => option.label}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => (
            <Chip {...getTagProps({ index })} key={tag.id} label={tag.label} size="small" sx={sectorChipSx} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} placeholder={value.length ? "" : "Type to search…"} sx={textareaSx} />
        )}
      />
    </Box>
  );
};

const SubsectorPicker = ({ sectors, selected, onChange, provenance, threshold }) => {
  const options = sectors
    .flatMap((sector) => companySubSectorMap[sector] || [])
    .filter((key, index, all) => all.indexOf(key) === index)
    .map((key) => ({ id: key, label: humanizeRegion(key) }));
  const value = selected.map((key) => options.find((o) => o.id === key) || { id: key, label: humanizeRegion(key) });

  return (
    <Box>
      <FieldLabel label="Subsector" provenance={provenance} threshold={threshold} />
      <Autocomplete
        multiple
        filterSelectedOptions
        disabled={sectors.length === 0}
        size="small"
        options={options}
        value={value}
        onChange={(_e, next) => onChange(next.map((o) => o.id))}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        getOptionLabel={(option) => option.label}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => (
            <Chip {...getTagProps({ index })} key={tag.id} label={tag.label} size="small" sx={sectorChipSx} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={sectors.length ? (value.length ? "" : "Type to search…") : "Pick a sector first"}
            sx={textareaSx}
          />
        )}
      />
    </Box>
  );
};

// Profile completion is stored on every candidate and drives filters elsewhere, but nothing ever
// showed it at the one moment someone can act on it. Doubles as the section nav, because the form
// is a single long scroll of seven sections.
const FormProgressBar = ({ completion, onJump, activeSection }) => (
  <Box
    sx={{
      // Sticky at the very top of the scrollport. No negative top margin: that would put its
      // natural position above the scrollport, and sticky would clamp it back down, leaving a gap.
      position: "sticky",
      top: 0,
      zIndex: 2,
      mx: -2.5,
      mb: 0.5,
      px: 2.5,
      py: 1.25,
      backgroundColor: "#fff",
      borderBottom: "1px solid hsl(140 8% 88%)",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
        Profile {completion}%
      </Typography>
      <LinearProgress
        variant="determinate"
        value={completion}
        sx={{
          flex: 1,
          height: 5,
          borderRadius: 3,
          backgroundColor: "hsl(140 8% 90%)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            backgroundColor: completion >= 70 ? "hsl(150 38% 17%)" : "hsl(35 92% 45%)",
          },
        }}
      />
    </Stack>

    <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
      {FORM_SECTIONS.map((section) => (
        <Box
          key={section.key}
          component="button"
          type="button"
          onClick={() => onJump(section.key)}
          sx={{
            border: "none",
            cursor: "pointer",
            borderRadius: "5px",
            px: 0.75,
            py: 0.25,
            fontSize: 11,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            backgroundColor: activeSection === section.key ? "hsl(150 38% 17%)" : "hsl(140 10% 95%)",
            color: activeSection === section.key ? "hsl(80 97% 72%)" : "hsl(150 10% 46%)",
            "&:hover": { backgroundColor: activeSection === section.key ? "hsl(150 38% 17%)" : "hsl(140 8% 90%)" },
          }}
        >
          {section.label}
        </Box>
      ))}
    </Stack>
  </Box>
);

// Everything the recruiter needs to know about a parse in flight: which step it is on, how long it
// has been going, and — when it fails — which of the two failures it was and what to do about it.
const ParseStatusPanel = ({
  status, stage, stageDetail, elapsed, attempt, error, errorKind, stalled, summary, expected,
  onRetry, onUndo, canUndo,
}) => {
  const running = ["queued", "parsing", "retrying"].includes(status);
  const failed = status === "failed" || status === "expired" || stalled;
  const currentIndex = Math.max(0, PARSE_STAGES.findIndex((s) => s.key === (stage || "queued")));
  const overdue = elapsed > expected.p90;

  if (failed) {
    const copy = status === "expired"
      ? { title: "This extraction is no longer available", detail: "It expired or was cleared. Start it again if you still need it.", action: "retry" }
      : errorCopy({ errorKind, error, stalled });

    return (
      <Paper elevation={0} sx={{ mt: 1, p: 1.5, borderRadius: "8px", border: "1px solid hsl(0 60% 85%)", backgroundColor: "hsl(0 80% 98%)" }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <AlertTriangle size={15} color="hsl(0 72% 51%)" style={{ marginTop: 2, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(0 72% 45%)" }}>
              {copy.title}
            </Typography>
            <Typography sx={{ fontSize: 12, mt: 0.25, color: "hsl(0 40% 40%)" }}>
              {copy.detail}
            </Typography>
            <Typography sx={{ fontSize: 12, mt: 0.75, color: "hsl(150 10% 46%)" }}>
              {copy.action === "manual"
                ? "Fill the form in by hand, the CV is on the right."
                : "The document is fine; the service was not."}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {copy.action === "retry" && (
                <Button
                  size="small"
                  disableRipple
                  startIcon={<RotateCcw size={13} />}
                  onClick={onRetry}
                  sx={{ textTransform: "none", fontSize: 12, py: 0.25, borderRadius: "6px", color: "hsl(150 38% 17%)", border: "1px solid hsl(150 20% 70%)" }}
                >
                  Try again
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (running) {
    return (
      <Paper elevation={0} sx={{ mt: 1, p: 1.5, borderRadius: "8px", border: "1px solid hsl(140 8% 88%)", backgroundColor: "hsl(140 10% 95%)" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={14} sx={{ color: "hsl(150 38% 17%)" }} />
          <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", flex: 1 }}>
            {PARSE_STAGES[currentIndex]?.label || "Working"}
            {attempt > 1 && ` retrying (attempt ${attempt})`}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", fontVariantNumeric: "tabular-nums" }}>
            {formatSeconds(elapsed)}
          </Typography>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={(currentIndex / (PARSE_STAGES.length - 1)) * 100}
          sx={{
            mt: 1, height: 3, borderRadius: 2, backgroundColor: "hsl(140 8% 90%)",
            "& .MuiLinearProgress-bar": { backgroundColor: "hsl(150 38% 17%)" },
          }}
        />

        {attempt > 1 && error && (
          <Typography sx={{ fontSize: 11, mt: 0.75, color: "hsl(35 60% 30%)" }}>
            First attempt failed: {error}
          </Typography>
        )}

        <Stack spacing={0.25} sx={{ mt: 1 }}>
          {PARSE_STAGES.slice(0, -1).map((s, index) => {
            const done = index < currentIndex;
            const current = index === currentIndex;
            const detail = current ? s.detail?.(stageDetail || {}) : null;

            return (
              <Stack key={s.key} direction="row" spacing={0.75} alignItems="baseline">
                <Typography sx={{ fontSize: 11, width: 12, flexShrink: 0, color: done ? "hsl(150 38% 17%)" : "hsl(150 10% 62%)" }}>
                  {done ? "✓" : current ? "●" : "○"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: done ? "hsl(150 38% 17%)" : current ? "hsl(150 30% 12%)" : "hsl(150 10% 62%)",
                    fontFamily: current ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                  }}
                >
                  {s.label}
                </Typography>
                {detail && (
                  <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)" }}>: {detail}</Typography>
                )}
              </Stack>
            );
          })}
        </Stack>

        <Typography sx={{ fontSize: 11, mt: 0.75, color: "hsl(150 10% 46%)" }}>
          {overdue
            ? `Longer than usual: 9 in 10 finish within ${formatSeconds(expected.p90)}. It is still running.`
            : `Usually ${formatSeconds(expected.p50)}–${formatSeconds(expected.p90)}. You can minimise this form and carry on.`}
        </Typography>
      </Paper>
    );
  }

  if (status !== "done" || !summary) return null;

  const needsEyes = summary.lowConfidence.length;

  return (
    <Paper elevation={0} sx={{ mt: 1, px: 1.5, pt: 1, pb: 1, borderRadius: "8px", border: "1px solid hsl(140 8% 88%)", backgroundColor: "hsl(140 10% 95%)" }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Check size={15} color="hsl(150 38% 17%)" />
        <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", flex: 1 }}>
          Form filled from the CV
        </Typography>
        {canUndo && (
          <Button
            size="small"
            disableRipple
            startIcon={<Undo2 size={13} />}
            onClick={onUndo}
            sx={{ textTransform: "none", fontSize: 12, py: 0, minWidth: 0, color: "hsl(150 10% 46%)", "&:hover": { backgroundColor: "transparent", color: "hsl(150 38% 17%)" } }}
          >
            Undo
          </Button>
        )}
      </Stack>

      <Typography sx={{ fontSize: 12, mt: 0.5, color: "hsl(150 10% 46%)" }}>
        {[
          summary.name,
          `${summary.fieldCount} field${summary.fieldCount === 1 ? "" : "s"}`,
          `${summary.experiences} experience${summary.experiences === 1 ? "" : "s"}`,
          `${summary.trainings} training${summary.trainings === 1 ? "" : "s"}`,
          `${summary.skills} skill${summary.skills === 1 ? "" : "s"}`,
        ].filter(Boolean).join(" · ")}
      </Typography>

      {needsEyes > 0 && (
        <Box sx={{ mt: 1, p: 1, borderRadius: "6px", backgroundColor: "hsl(40 100% 95%)", border: "1px solid hsl(40 80% 82%)" }}>
          <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(35 92% 30%)" }}>
            {needsEyes} field{needsEyes === 1 ? "" : "s"} the parser was unsure of
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 0.25, color: "hsl(35 60% 30%)" }}>
            {summary.lowConfidence.map((f) => humanizeRegion(f.field)).join(", ")}: check {needsEyes === 1 ? "it" : "them"} against the CV before saving.
          </Typography>
        </Box>
      )}

      {summary.extras.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5 }}>
            Also found, no field for these yet
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
            {summary.extras.map((extra) => (
              <Chip
                key={extra.label}
                size="small"
                label={`${extra.label}: ${extra.value}`}
                sx={{ fontSize: 11, backgroundColor: "#fff", border: "1px solid hsl(140 8% 88%)" }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {summary.droppedSkills.length > 0 && (
        <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1 }}>
          <FileWarning size={13} color="hsl(35 92% 33%)" style={{ marginTop: 2, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: "hsl(35 60% 30%)" }}>
            Not added, outside our skill list: {summary.droppedSkills.join(", ")}
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

const ResumeChoiceCard = ({ selected, onSelect, title, subtitle, filename, warning }) => (
  <Box
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect()}
    sx={{
      p: 1.25,
      borderRadius: "8px",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
      border: `1px solid ${selected ? "hsl(150 38% 17%)" : "hsl(140 8% 88%)"}`,
      backgroundColor: selected ? "hsl(150 38% 97%)" : "#ffffff",
      boxShadow: selected ? "0 0 0 1px hsl(150 38% 17%)" : "none",
      transition: "border-color 0.15s ease, background-color 0.15s ease",
      "&:hover": { backgroundColor: selected ? "hsl(150 38% 97%)" : "hsl(140 10% 97%)" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 14,
          height: 14,
          flexShrink: 0,
          borderRadius: "50%",
          border: `1.5px solid ${selected ? "hsl(150 38% 17%)" : "hsl(140 8% 75%)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {selected && <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "hsl(150 38% 17%)" }} />}
      </Box>
      <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
        {title}
      </Typography>
      {warning && <AlertTriangle size={12} color="hsl(35 60% 32%)" style={{ flexShrink: 0 }} />}
    </Box>
    <Typography sx={{ fontSize: 11.5, color: warning ? "hsl(35 60% 32%)" : "hsl(150 10% 46%)", lineHeight: 1.35 }}>
      {subtitle}
    </Typography>
    {filename && (
      <Typography noWrap sx={{ fontSize: 11, color: "hsl(150 10% 55%)" }}>
        {filename}
      </Typography>
    )}
  </Box>
);

const CreateCandidateModal = ({ open, onClose, onSubmit, submitting, defaultValues, defaultResumeFile, isEditMode = false, existingResumeUrl = null, candidateId = null, cvVaultItemId = null, cvFilename = null, parsedPayload = null, initialCvText = null }) => {
  const regionGroupsCreate = useMemo(
    () => [
      { label: formatRegionMessage("belgium"), regions: be_regions },
      { label: formatRegionMessage("luxembourg"), regions: lu_regions },
      { label: formatRegionMessage("france"), regions: fr_regions },
    ],
    [],
  );

  const currentInternalUser = useSelector((state) => state.current_internal_user);
  const internalUserFullName = useMemo(
    () => [currentInternalUser?.first_name, currentInternalUser?.last_name].filter(Boolean).join(" ") || "Squarehub Team",
    [currentInternalUser],
  );

  const [values, setValues] = useState(initialValues);
  const [resumeFile, setResumeFile] = useState(null);

  // "Extract CV": a parse of the picked file, running before any candidate exists.
  const [parseToken, setParseToken] = useState(null);
  const [parserModel, setParserModel] = useState(DEFAULT_PARSER_MODEL);
  const [parseStatus, setParseStatus] = useState("idle");
  const [parseStage, setParseStage] = useState(null);
  const [parseStageDetail, setParseStageDetail] = useState({});
  const [parseExpected, setParseExpected] = useState(DEFAULT_EXPECTED);
  const [parseAttempt, setParseAttempt] = useState(1);
  const [parseElapsed, setParseElapsed] = useState(0);
  const [parseStalled, setParseStalled] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [parseErrorKind, setParseErrorKind] = useState(null);
  const [parseSummary, setParseSummary] = useState(null);
  const [provenance, setProvenance] = useState({});
  const [threshold, setThreshold] = useState(DEFAULT_CONFIDENCE_THRESHOLD);
  const [preParseSnapshot, setPreParseSnapshot] = useState(null);
  // Set only when the CV disagrees with a value already on the form; additions apply straight away.
  const [pendingReview, setPendingReview] = useState(null);
  const [cvText, setCvText] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [formPct, setFormPct] = useState(() => {
    const saved = Number(window.localStorage?.getItem(SPLIT_STORAGE_KEY));
    return saved >= MIN_PANE_PCT && saved <= MAX_PANE_PCT ? saved : DEFAULT_FORM_PCT;
  });
  const [resizing, setResizing] = useState(false);
  const splitRef = useRef(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [touched, setTouched] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");

  const [experiences, setExperiences] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [skills, setSkills] = useState([]);
  const [certificates, setCertificates] = useState([]);
  // Rows the user deleted that already exist in the DB: they come back as `_destroy` on submit.
  const [removedExperienceIds, setRemovedExperienceIds] = useState([]);
  const [removedTrainingIds, setRemovedTrainingIds] = useState([]);
  const [removedCertificateIds, setRemovedCertificateIds] = useState([]);

  const [sendAutoEmail, setSendAutoEmail] = useState(false);
  const [autoEmailLocale, setAutoEmailLocale] = useState("fr");
  const [selectedJobOffers, setSelectedJobOffers] = useState([]);
  const [customEmailBody, setCustomEmailBody] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [offerSearch, setOfferSearch] = useState("");
  const [offerResults, setOfferResults] = useState([]);
  const [searchingOffers, setSearchingOffers] = useState(false);

  const generateDefaultEmailHTML = useCallback((locale, password) => {
    const name = values.first_name || values.last_name || "";
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://squarehub.eu";

    if (locale === "nl") {
      return `<p>Beste ${name},</p>
<p><br>Bedankt voor je tijd daarnet. Zoals besproken bevestig ik het aanmaak van uw account op het Squarehub-platform.<br>Om in te loggen, gebruikt u uw e-mailadres en het volgende wachtwoord: <strong>${password}</strong> (vergeet niet u wachtwoord te veranderen).</p>
<p><br>Hier is de link naar de website: <a href="https://squarehub.eu/nl">https://squarehub.eu/nl</a><br/><br/>Ik raad u aan uw profiel zo volledig mogelijk in te vullen en uw cv toe te voegen, zodat u beter zichtbaar bent voor bedrijven.</p>
${selectedJobOffers.length > 0 ? `<p>Naar aanleiding van ons telefoongesprek, hierbij de link naar de vacature${selectedJobOffers.length > 1 ? "s" : ""} die we hebben besproken:</p><ul>${selectedJobOffers.map(o => `<li><a href="${baseUrl}/nl/job-offers/${o.id}">${o.company} - ${o.title}</a></li>`).join("")}</ul><p><br>Kan je ${selectedJobOffers.length > 1 ? "deze" : "dit"} even doornemen en laten weten of ${selectedJobOffers.length > 1 ? "er iets" : "dit eventueel een match"} zou kunnen zijn? Zo ja, dan stel ik je graag meteen voor aan onze partner.</p>` : ""}
<p><br>Als start-up hebben we altijd behoefte aan meer zichtbaarheid. Aarzel niet om ons 5* te geven op Google via de volgende link: <a href="https://g.page/r/CUP6WW7FvQk6EB0/review">https://g.page/r/CUP6WW7FvQk6EB0/review</a></p>
<p><br>Alvast bedankt en tot snel<br>Met vriendelijke groeten,</p>
<p><br>${internalUserFullName}<br>+32 (0) 3 215 14 31<br>E: <a href="mailto:talent@squarehub.eu">talent@squarehub.eu</a><br>Squarehub - Bd du Triomphe 174, 1160 Brussels</p>
<p><a href="${baseUrl}/nl/">Squarehub</a> | <a href="https://www.linkedin.com/company/squarehub-eu/?viewAsMember=true">LinkedIn</a> | <a href="${baseUrl}/nl/privacy-policy">Privacy Policy</a></p>`;
    } else {
      return `<p>Bienvenue sur notre plateforme, ${name} !</p>
<p><br>Merci pour ton temps. Comme convenu, je confirme la création de ton compte sur la plateforme Squarehub.<br>Pour te connecter, utilise ton adresse e-mail et le mot de passe suivant : <strong>${password}</strong> (n'oublie pas de changer ton mot de passe).</p>
<p><br>Voici le lien vers le site : <a href="${baseUrl}/fr">https://squarehub.eu/fr</a></p>
<p><br>Je te conseille de compléter ton profil au maximum et d'ajouter ton CV afin d'être mieux visible par les entreprises.</p>
${selectedJobOffers.length > 0 ? `<p>Suite à notre échange téléphonique, voici les liens vers les offres dont nous avons parlé :</p><ul>${selectedJobOffers.map(o => `<li><a href="${baseUrl}/fr/job-offers/${o.id}">${o.company} - ${o.title}</a></li>`).join("")}</ul><p><br>Peux-tu les parcourir et me dire si cela pourrait correspondre ?</p>` : ""}
<p><br>En tant que start-up, nous avons toujours besoin de plus de visibilité, n'hésite pas à nous laisser 5* sur Google via le lien suivant : <a href="https://g.page/r/CUP6WW7FvQk6EB0/review">https://g.page/r/CUP6WW7FvQk6EB0/review</a></p>
<p><br>Merci d'avance et à bientôt<br/>Bien à toi,<br/><br/>${internalUserFullName}<br/>+32 (0) 2 329 11 00<br/>E: <a href="mailto:contact@squarehub.eu">contact@squarehub.eu</a><br/>Squarehub - Bd du Triomphe 174, 1160 Brussels</p>
<p><a href="${baseUrl}/fr/">Squarehub</a> | <a href="https://www.linkedin.com/company/squarehub-eu/?viewAsMember=true">LinkedIn</a> | <a href="${baseUrl}/fr/privacy-policy">Privacy Policy</a></p>`;
    }
  }, [values.first_name, values.last_name, selectedJobOffers, internalUserFullName]);

  useEffect(() => {
    if (sendAutoEmail && !generatedPassword) {
      setGeneratedPassword(DEFAULT_CANDIDATE_PASSWORD);
    }
  }, [sendAutoEmail, generatedPassword]);

  useEffect(() => {
    if (sendAutoEmail && generatedPassword) {
      setCustomEmailBody(generateDefaultEmailHTML(autoEmailLocale, generatedPassword));
    }
  }, [sendAutoEmail, autoEmailLocale, generatedPassword, generateDefaultEmailHTML]);

  useEffect(() => {
    if (open) {
      if (defaultValues) {
        const { experiences: exps, trainings: trs, skills: sks, certificates: certs, ...scalars } = defaultValues;
        setValues((prev) => ({
          ...prev,
          ...scalars,
          candidate_status: scalars.candidate_status === "independent_type" ? "independent" : scalars.candidate_status,
          // Legacy rows hold free text ("5-10 years"); show what will actually be saved.
          experience_year: String(scalars.experience_year || "").replace(/\D/g, ""),
        }));
        if (Array.isArray(exps)) setExperiences(exps.map(toExperienceRow));
        if (Array.isArray(trs)) setTrainings(trs.map(toTrainingRow));
        if (Array.isArray(sks)) setSkills(sks.map(({ name, category }) => ({ name, category })));
        if (Array.isArray(certs)) setCertificates(certs.map(({ id, name, category }) => ({ id, name, category })));
      }
      if (defaultResumeFile) setResumeFile(defaultResumeFile);
      if (initialCvText) setCvText(initialCvText);
      if (parsedPayload) {
        applyParsedPayload(parsedPayload, parsedPayload.detected_language);
        setParseStatus("done");
      }
    } else {
      setValues(initialValues);
      setResumeFile(null);
      setShowErrors(false);
      setTouched({});
      setDragActive(false);
      setActiveSection("basic");
      setParseToken(null);
      setParseStatus("idle");
      setParseStage(null);
      setParseStageDetail({});
      setParseExpected(DEFAULT_EXPECTED);
      setParseAttempt(1);
      setParseElapsed(0);
      setParseStalled(false);
      setParseError(null);
      setParseErrorKind(null);
      setParseSummary(null);
      setProvenance({});
      setThreshold(DEFAULT_CONFIDENCE_THRESHOLD);
      setPreParseSnapshot(null);
      setPendingReview(null);
      setCvText(null);
      setMinimized(false);
      setConfirmCloseOpen(false);
      setExperiences([]);
      setTrainings([]);
      setSkills([]);
      setCertificates([]);
      setRemovedExperienceIds([]);
      setRemovedTrainingIds([]);
      setRemovedCertificateIds([]);
      setSendAutoEmail(false);
      setAutoEmailLocale("fr");
      setSelectedJobOffers([]);
      setCustomEmailBody("");
      setGeneratedPassword("");
      setOfferSearch("");
      setOfferResults([]);
    }
  }, [open, defaultValues, defaultResumeFile, parsedPayload, initialCvText]);

  // The two the candidates table actually enforces (both NOT NULL), so they are the two that carry
  // an asterisk and block the save. Everything else the talent team fills in as it learns it.
  const errors = useMemo(() => {
    const next = {};
    if (!values.first_name.trim()) next.first_name = "First name is required";
    if (!values.email.trim()) next.email = "Email is required";
    else if (!EMAIL_RE.test(values.email.trim())) next.email = "Enter a valid email address";
    return next;
  }, [values.email, values.first_name]);

  const incompleteExperiences = useMemo(
    () => experiences.filter((r) => !r.title || !r.company || !r.start_date).length,
    [experiences],
  );
  const incompleteTrainings = useMemo(
    () => trainings.filter((r) => !r.diploma).length,
    [trainings],
  );
  const invalidExperienceDates = useMemo(
    () => experiences.filter((r) => isDateOrderInvalid(r)).length,
    [experiences],
  );
  const invalidTrainingDates = useMemo(
    () => trainings.filter((r) => isDateOrderInvalid(r)).length,
    [trainings],
  );

  const canSubmit = Object.keys(errors).length === 0 && incompleteExperiences === 0 && incompleteTrainings === 0
    && invalidExperienceDates === 0 && invalidTrainingDates === 0;

  const blockingReason = useMemo(() => {
    const reasons = [];
    if (errors.first_name || errors.email) reasons.push("a first name and a valid email");
    if (incompleteExperiences > 0) {
      reasons.push(
        `${incompleteExperiences} experience${incompleteExperiences === 1 ? "" : "s"} missing a job title, company or start date`,
      );
    }
    if (incompleteTrainings > 0) {
      reasons.push(`${incompleteTrainings} training${incompleteTrainings === 1 ? "" : "s"} missing a diploma`);
    }
    if (invalidExperienceDates > 0) {
      reasons.push(`${invalidExperienceDates} experience${invalidExperienceDates === 1 ? "" : "s"} with an end date before the start date`);
    }
    if (invalidTrainingDates > 0) {
      reasons.push(`${invalidTrainingDates} training${invalidTrainingDates === 1 ? "" : "s"} with an end date before the start date`);
    }
    if (reasons.length === 0) return "";

    return `Still needed: ${reasons.join(", ")}.`;
  }, [errors.first_name, errors.email, incompleteExperiences, incompleteTrainings, invalidExperienceDates, invalidTrainingDates]);

  useEffect(() => {
    if (parseStatus === "done" && (incompleteExperiences > 0 || incompleteTrainings > 0)) {
      setShowErrors(true);
    }
  }, [parseStatus, incompleteExperiences, incompleteTrainings]);

  const completion = useMemo(() => {
    const present = {
      resume: Boolean(resumeFile || existingResumeUrl),
      experiences: experiences.length > 0,
      trainings: trainings.length > 0,
      skills: skills.length > 0,
      desired_sector: (values.desired_sector || []).length > 0,
    };
    const earned = COMPLETION_WEIGHTS.reduce((sum, item) => {
      const filled = item.key in present ? present[item.key] : Boolean(String(values[item.key] || "").trim());
      return filled ? sum + item.weight : sum;
    }, 0);

    return Math.round((earned / COMPLETION_TOTAL) * 100);
  }, [values, resumeFile, existingResumeUrl, experiences, trainings, skills]);

  const candidateStatusOptions = useMemo(() => {
    const current = values.candidate_status;
    if (current && current !== "independent_type" && !CANDIDATE_STATUS_OPTIONS.includes(current)) {
      return [...CANDIDATE_STATUS_OPTIONS, current];
    }
    return CANDIDATE_STATUS_OPTIONS;
  }, [values.candidate_status]);

  const regionToCountry = (region) => {
    if (be_regions.includes(region)) return "belgium";
    if (fr_regions.includes(region)) return "france";
    if (lu_regions.includes(region)) return "luxembourg";
    return "";
  };

  // Typing in a field makes it the recruiter's, so the badge goes: it claims the value came from
  // the CV, and once they have overridden it that is no longer true.
  const clearProvenance = (key) =>
    setProvenance((prev) => (key in prev ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key)) : prev));

  const set = (key) => (value) => {
    clearProvenance(key);
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  // Errors show once a field has been left, rather than only after a failed save at the bottom of
  // a thirty-field form.
  const touch = (key) => () => setTouched((prev) => ({ ...prev, [key]: true }));

  const setRegion = (value) => {
    clearProvenance("region");
    const inferred = regionToCountry(value);
    setValues((prev) => ({ ...prev, region: value, ...(inferred && { country: inferred }) }));
  };

  const setDesiredSector = (nextSectors) => {
    clearProvenance("desired_sector");
    setValues((prev) => {
      const allowed = new Set(nextSectors.flatMap((sector) => companySubSectorMap[sector] || []));
      return {
        ...prev,
        desired_sector: nextSectors,
        subsectors: (prev.subsectors || []).filter((key) => allowed.has(key)),
      };
    });
  };

  // `experience_year` is a string column but only ever holds a whole number of years, so digits are
  // the only thing the field accepts and the value is stringified again on submit.
  const setExperienceYear = (value) => {
    clearProvenance("experience_year");
    setValues((prev) => ({ ...prev, experience_year: value.replace(/\D/g, "") }));
  };

  const setSource = (value) =>
    setValues((prev) => ({ ...prev, source: value, source_other: value === "other" ? prev.source_other : "" }));

  const updateBool = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.checked }));

  const updateExperience = (index, row) =>
    setExperiences((prev) => prev.map((r, i) => (i === index ? row : r)));
  const removeExperience = (index) =>
    setExperiences((prev) => {
      const row = prev[index];
      if (row?.id) setRemovedExperienceIds((ids) => [...ids, row.id]);
      return prev.filter((_r, i) => i !== index);
    });

  const updateTraining = (index, row) =>
    setTrainings((prev) => prev.map((r, i) => (i === index ? row : r)));
  const removeTraining = (index) =>
    setTrainings((prev) => {
      const row = prev[index];
      if (row?.id) setRemovedTrainingIds((ids) => [...ids, row.id]);
      return prev.filter((_r, i) => i !== index);
    });

  const handleCertificatesChange = (nextOptions) =>
    setCertificates((prev) => {
      const next = nextOptions.map(
        (o) => prev.find((c) => c.name === o.id && c.category === o.category) || { name: o.id, category: o.category },
      );
      const removed = prev.filter((c) => c.id && !next.some((n) => n.name === c.name && n.category === c.category));
      if (removed.length) setRemovedCertificateIds((ids) => [...ids, ...removed.map((c) => c.id)]);
      return next;
    });

  const selectedSkillsFor = useCallback(
    (category) => skills.filter((s) => s.category === category).map((s) => s.name),
    [skills],
  );

  const setSkillsFor = (category, names) =>
    setSkills((prev) => [
      ...prev.filter((s) => s.category !== category),
      ...names.map((name) => ({ name, category })),
    ]);

  const parsing = ["queued", "parsing", "retrying"].includes(parseStatus);

  const valuesRef = useRef(values);
  const experiencesRef = useRef(experiences);
  const trainingsRef = useRef(trainings);
  const skillsRef = useRef(skills);
  valuesRef.current = values;
  experiencesRef.current = experiences;
  trainingsRef.current = trainings;
  skillsRef.current = skills;

  // The picked file is already in the browser, so the viewer costs nothing: an object URL renders
  // it in Chrome's own PDF viewer. Revoked on change so a long session does not leak the blobs.
  const [resumeObjectUrl, setResumeObjectUrl] = useState(null);
  useEffect(() => {
    if (!resumeFile) {
      setResumeObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(resumeFile);
    setResumeObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [resumeFile]);

  const [branded, setBranded] = useState(null);
  const [resumeSource, setResumeSource] = useState("original");
  const [editingBranded, setEditingBranded] = useState(false);
  const [brandedVersion, setBrandedVersion] = useState(0);

  const brandedSourceUrl = cvVaultItemId
    ? `/internal/cv_vault_items/${cvVaultItemId}/branded_cv`
    : candidateId
      ? `/internal/candidates/${candidateId}/branded_cv`
      : null;
  // A vault item only exists once a file has been uploaded to it, so cvVaultItemId alone already
  // means there is an original CV. An existing candidate needs its own resume checked for.
  const hasOriginalCv = Boolean(cvVaultItemId || existingResumeUrl);

  useEffect(() => {
    if (!open || !brandedSourceUrl) {
      setBranded(null);
      setResumeSource("original");
      setEditingBranded(false);
      setBrandedVersion(0);
      return undefined;
    }

    let active = true;
    sh.get(brandedSourceUrl)
      .then((res) => active && setBranded(res.data?.status ? res.data : null))
      .catch(() => active && setBranded(null));

    return () => {
      active = false;
    };
  }, [open, brandedSourceUrl]);

  // No ActionCable channel reaches an internal recruiter for either source (CvVaultItemsChannel is
  // internal-user-wide, not scoped to one candidate; CandidateChannel is candidate-only), so a
  // generation in progress is tracked by polling instead.
  useEffect(() => {
    if (!open || !brandedSourceUrl || branded?.status !== "generating") return undefined;

    const id = setInterval(() => {
      sh.get(brandedSourceUrl).then((res) => setBranded(res.data?.status ? res.data : null)).catch(() => {});
    }, 3000);

    return () => clearInterval(id);
  }, [open, brandedSourceUrl, branded?.status]);

  const generateBrandedCv = (locale) => {
    if (!brandedSourceUrl || !hasOriginalCv) return;

    sh.post(brandedSourceUrl, {
      parser_model: parserModel,
      overrides: locale ? { locale } : undefined,
    })
      .then(() => {
        setBranded((prev) => ({ ...(prev || {}), status: "generating", error: null }));
        enqueueSnackbar("Writing the Squarehub CV", { variant: "success" });
      })
      .catch(() => enqueueSnackbar("Could not start the Squarehub CV", { variant: "error" }));
  };

  const brandedReady = branded?.status === "ready";

  const brandedIncomplete = brandedReady && isIncomplete(branded?.data);
  const brandedUrl = cvVaultItemId
    ? `/internal/cv_vault_items/${cvVaultItemId}/branded_cv/download?v=${brandedVersion}`
    : candidateId
      ? `/internal/candidates/${candidateId}/branded_cv/download?v=${brandedVersion}`
      : null;

  const sectionRefs = useRef({});
  // State, not a ref: the dialog's children are not in the DOM on the first pass, so an effect
  // keyed on `open` alone would run once against null and never see the container appear.
  const [scrollEl, setScrollEl] = useState(null);
  // Scrolls the pane itself rather than calling scrollIntoView, which also scrolls every ancestor
  // and would leave the target hidden under the sticky bar.
  const jumpToSection = (key) => {
    setActiveSection(key);

    const el = sectionRefs.current[key];
    if (!scrollEl || !el) return;

    const offset = el.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top;
    scrollEl.scrollTo({
      top: Math.max(0, scrollEl.scrollTop + offset - STICKY_NAV_OFFSET),
      behavior: "smooth",
    });
  };

  const acceptDroppedFile = useCallback((file) => {
    if (!file || !ACCEPTED_CV.test(file.name)) return;
    setResumeFile(file);
    jumpToSection("cv");
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    acceptDroppedFile(e.dataTransfer?.files?.[0]);
  };

  // Dragging over a child fires dragleave on the parent, so the overlay is keyed off a counter
  // rather than the raw events — otherwise it flickers the moment the cursor crosses a field.
  const dragDepth = useRef(0);
  const handleDragEnter = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    dragDepth.current += 1;
    setDragActive(true);
  };
  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  };

  // Keeps the highlighted chip honest when the recruiter scrolls rather than clicks.
  useEffect(() => {
    if (!open || minimized || !scrollEl) return undefined;

    const onScroll = () => {
      const cutoff = scrollEl.getBoundingClientRect().top + STICKY_NAV_OFFSET;
      const current = FORM_SECTIONS.filter(({ key }) => {
        const el = sectionRefs.current[key];
        return el && el.getBoundingClientRect().top <= cutoff;
      }).pop();
      setActiveSection(current?.key || FORM_SECTIONS[0].key);
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [open, minimized, scrollEl]);

  // Paste a CV straight from the clipboard — copying a file in the OS file manager and hitting
  // Ctrl+V is how a lot of these arrive, and it saves the round trip through a file picker.
  useEffect(() => {
    if (!open || minimized) return undefined;

    const onPaste = (e) => {
      const target = e.target;
      // Never steal a paste aimed at a field the recruiter is typing in.
      if (target?.closest?.("input, textarea, [contenteditable='true']")) return;
      const file = Array.from(e.clipboardData?.files || [])[0];
      if (file) {
        e.preventDefault();
        acceptDroppedFile(file);
      }
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open, minimized, acceptDroppedFile]);

  const showingBranded = resumeSource === "branded" && brandedReady;

  // A vault CV is streamed through the app rather than from its blob url: the blob url carries a
  // content-disposition the browser honours, so a DOCX downloads instead of rendering.
  const originalName = resumeFile?.name || cvFilename || "";
  const originalIsDocx = /\.docx$/i.test(originalName);
  const originalUrl = resumeObjectUrl
    || (cvVaultItemId ? `/internal/cv_vault_items/${cvVaultItemId}/file` : existingResumeUrl);

  const viewerUrl = showingBranded ? brandedUrl : originalUrl;
  const viewerIsDocx = showingBranded ? false : originalIsDocx;
  const viewerIsPdf = showingBranded
    ? true
    : resumeFile
      ? /\.pdf$/i.test(resumeFile.name) || resumeFile.type === "application/pdf"
      : Boolean(originalUrl) && !originalIsDocx;
  const showViewer = Boolean(viewerUrl || cvText);

  // Fill-blanks-only, matching what the server does when it applies a parse to a saved candidate:
  // extracting a CV must never overwrite something the recruiter has already typed.
  const applyParsedPayload = (payload, detectedLanguage) => {
    const fields = payload?.candidate || {};
    const activeThreshold = payload?.confidence_threshold || DEFAULT_CONFIDENCE_THRESHOLD;
    const parsedValue = (key) => {
      const raw = fields[key]?.value;
      return Array.isArray(raw) ? raw[0] : raw;
    };

    // Read through refs, not the closure: this runs from the polling effect, which was created
    // when the parse started and would otherwise apply the parse on top of a stale form.
    const current = valuesRef.current;
    setPreParseSnapshot({
      values: current,
      experiences: experiencesRef.current,
      trainings: trainingsRef.current,
      skills: skillsRef.current,
    });
    setThreshold(activeThreshold);

    const next = { ...current };
    const filled = {};
    const scalarConflicts = [];
    PARSED_FIELDS.forEach((key) => {
      const incoming = parsedValue(key);
      if (!incoming) return;

      if (!next[key]) {
        next[key] = String(incoming);
        filled[key] = fields[key];
      } else if (CONFLICT_FIELDS.includes(key) && normalizeForCompare(next[key]) !== normalizeForCompare(incoming)) {
        scalarConflicts.push({
          id: `scalar:${key}`,
          kind: "scalar",
          field: key,
          label: FIELD_LABELS[key] || humanizeRegion(key),
          current: next[key],
          proposed: String(incoming),
          confidence: fields[key]?.confidence ?? null,
          snippet: fields[key]?.snippet ?? null,
        });
      }
    });
    // ai_summary has no column of its own: on submit it is folded into recruiter_summaries under
    // this locale, the language the parser wrote it in, not the candidate's own words in `presentation`.
    if (filled.ai_summary && !next.ai_summary_locale) {
      next.ai_summary_locale = detectedLanguage || payload?.detected_language || "";
    }
    // extras.portfolio_url has a column and a field on this form; it used to be dropped because
    // only payload.candidate was read.
    const portfolio = payload?.extras?.portfolio_url;
    if (!next.portfolio_url && portfolio) next.portfolio_url = String(portfolio);

    const parsedSectors = fields.desired_sector?.value || [];
    if (parsedSectors.length && !(next.desired_sector || []).length) {
      next.desired_sector = parsedSectors;
      filled.desired_sector = fields.desired_sector;
    }
    const parsedSubsectors = fields.subsectors?.value || [];
    if (parsedSubsectors.length && !(next.subsectors || []).length) {
      next.subsectors = parsedSubsectors;
      filled.subsectors = fields.subsectors;
    }

    setProvenance(filled);

    // Existing rows are never replaced wholesale, only added to. A row matched by company +
    // start date with a different title is a conflict, not an addition.
    const parsedExperiences = payload?.experiences || [];
    const parsedTrainings = payload?.trainings || [];
    const parsedSkills = payload?.skills || [];
    // A skill the taxonomy does not list is dropped by the server on save, so it is reported here
    // rather than shown in the picker and silently lost.
    const knownSkill = (name, category) =>
      (SKILL_OPTIONS[category] || []).some((option) => option.id === name);
    const acceptedSkills = parsedSkills.filter((s) => knownSkill(s.name, s.category));
    const droppedSkills = parsedSkills.filter((s) => !knownSkill(s.name, s.category)).map((s) => s.name);

    const currentExperiences = experiencesRef.current;
    const experienceConflicts = [];
    const newExperiences = [];
    parsedExperiences.forEach((exp) => {
      const match = exp.company && exp.start_date
        ? currentExperiences.find((row) => normalizeForCompare(row.company) === normalizeForCompare(exp.company)
          && String(row.start_date || "") === String(exp.start_date || ""))
        : null;

      if (!match) {
        newExperiences.push(exp);
      } else if (exp.title && normalizeForCompare(match.title) !== normalizeForCompare(exp.title)) {
        experienceConflicts.push({
          id: `experience:${match._key}`,
          kind: "experience",
          rowKey: match._key,
          company: exp.company,
          dates: formatExperienceDates(exp.start_date, exp.end_date),
          label: "Job title",
          current: match.title,
          proposed: exp.title,
          confidence: exp.confidence ?? null,
          snippet: null,
        });
      }
    });
    const experienceAdditions = newExperiences;

    const currentTrainings = trainingsRef.current;
    const trainingKey = (t) => `${normalizeForCompare(t.institution)}|${normalizeForCompare(t.study_field)}|${t.start_date || ""}`;
    const existingTrainingKeys = new Set(currentTrainings.map(trainingKey));
    const trainingAdditions = parsedTrainings.filter((t) => !existingTrainingKeys.has(trainingKey(t)));

    const currentSkills = skillsRef.current;
    const existingSkillNames = new Set(currentSkills.map((s) => normalizeForCompare(s.name)));
    const skillAdditions = acceptedSkills.filter((s) => !existingSkillNames.has(normalizeForCompare(s.name)));

    setValues(next);
    setExperiences([...currentExperiences, ...experienceAdditions.map(toExperienceRow)]);
    setTrainings([...currentTrainings, ...trainingAdditions.map(toTrainingRow)]);
    setSkills([
      ...currentSkills,
      ...skillAdditions.map(({ name, category, level }) => ({ name, category, level })),
    ]);

    const extras = Object.entries(payload?.extras || {})
      .filter(([key, value]) => EXTRA_LABELS[key] && value != null && value !== "" && (!Array.isArray(value) || value.length))
      .map(([key, value]) => ({ label: EXTRA_LABELS[key], value: humanizeExtra(value) }));

    setParseSummary({
      experiences: experienceAdditions.length,
      trainings: trainingAdditions.length,
      skills: skillAdditions.length,
      fieldCount: Object.keys(filled).length,
      name: [parsedValue("first_name"), parsedValue("last_name")].filter(Boolean).join(" "),
      lowConfidence: Object.entries(filled)
        .filter(([, data]) => data?.confidence != null && data.confidence < activeThreshold)
        .map(([field, data]) => ({ field, confidence: data.confidence })),
      extras,
      droppedSkills,
    });

    const additionsCount = Object.keys(filled).length + skillAdditions.length
      + experienceAdditions.length + trainingAdditions.length;

    if (scalarConflicts.length === 0 && experienceConflicts.length === 0) {
      setPendingReview(null);
    } else {
      setPendingReview({ scalarConflicts, experienceConflicts, additionsCount });
    }
  };

  const resolveConflicts = (resolvedList) => {
    const changedScalars = resolvedList.filter((c) => c.kind === "scalar" && c.choice === "cv");
    const changedExperiences = resolvedList.filter((c) => c.kind === "experience" && c.choice === "cv");

    if (changedScalars.length > 0) {
      setValues((prev) => {
        const next = { ...prev };
        changedScalars.forEach((c) => { next[c.field] = c.proposed; });
        return next;
      });
    }
    if (changedExperiences.length > 0) {
      setExperiences((prev) => prev.map((row) => {
        const match = changedExperiences.find((c) => c.rowKey === row._key);
        return match ? { ...row, title: match.proposed } : row;
      }));
    }

    const changedCount = changedScalars.length + changedExperiences.length;
    enqueueSnackbar(
      changedCount > 0
        ? `CV update applied. ${changedCount} value${changedCount === 1 ? "" : "s"} updated.`
        : "CV update reviewed. Existing values kept.",
      { variant: "success" },
    );
    setPendingReview(null);
  };

  const startExtract = (model = parserModel) => {
    if (!resumeFile && !candidateId && !cvVaultItemId) return;

    setParseToken(null);
    setParseStatus("queued");
    setParseStage("uploading");
    setParseStageDetail({});
    setParseAttempt(1);
    setParseElapsed(0);
    setParseStalled(false);
    setParseError(null);
    setParseErrorKind(null);
    setParseSummary(null);
    setProvenance({});

    const formData = new FormData();
    if (resumeFile) {
      formData.append("file", resumeFile);
    } else if (candidateId) {
      formData.append("candidate_id", candidateId);
    } else {
      formData.append("cv_vault_item_id", cvVaultItemId);
    }
    if (values.preferred_locale) formData.append("locale_hint", values.preferred_locale);
    // Belgian and Luxembourgish postcodes are both four digits; without this the parser cannot tell
    // "2000 Antwerpen" from a Luxembourgish code and falls back to guessing the province.
    if (values.country) formData.append("country", values.country);
    formData.append("parser_model", model);

    sh.post("/internal/cv_parse_previews", formData)
      .then((res) => {
        setParseToken(res.data?.token || null);
        setParseStage(res.data?.stage || "queued");
        if (res.data?.expected) setParseExpected(res.data.expected);
        setCvText(res.data?.cv_text || null);
      })
      .catch((err) => {
        const data = err?.response?.data || {};
        setParseStatus("failed");
        setParseError(data.error || "Could not start the extraction");
        setParseErrorKind(data.error_kind || "transient");
      });
  };

  // Fire-and-forget: the worker checks for the cancellation before it writes anything, so there is
  // nothing here that has to succeed for the recruiter to move on.
  const cancelExtract = useCallback((token) => {
    const target = token || parseToken;
    if (target) sh.delete(`/internal/cv_parse_previews/${target}`).catch(() => {});
    setParseToken(null);
    setParseStatus("idle");
    setParseStage(null);
    setParseStageDetail({});
    setParseStalled(false);
    setParseError(null);
    setParseErrorKind(null);
    setParseSummary(null);
  }, [parseToken]);

  const undoParse = () => {
    if (!preParseSnapshot) return;
    setValues(preParseSnapshot.values);
    setExperiences(preParseSnapshot.experiences);
    setTrainings(preParseSnapshot.trainings);
    setSkills(preParseSnapshot.skills);
    setPreParseSnapshot(null);
    setPendingReview(null);
    setProvenance({});
    setParseSummary(null);
    setParseStatus("idle");
  };

  useEffect(() => {
    if (!parseToken || !parsing) return undefined;

    const subscription = consumer.subscriptions.create(
      { channel: "CvParsePreviewChannel", id: parseToken },
      {
        received(data) {
          setParseStage(data.stage || null);
          setParseStageDetail(data.stage_detail || {});
          setParseElapsed(data.elapsed || 0);
          setParseAttempt(data.attempt || 1);
          setParseStalled(Boolean(data.stalled));

          if (data.status === "done") {
            applyParsedPayload(data.payload, data.detected_language);
            setParseStatus("done");
          } else if (data.status === "failed") {
            setParseStatus("failed");
            setParseError(data.error || "The CV could not be parsed");
            setParseErrorKind(data.error_kind || "transient");
          } else if (data.status === "cancelled") {
            setParseStatus("idle");
          }
          // `retrying` deliberately keeps the subscription open: Sidekiq has another attempt
          // scheduled, and stopping here would throw away a retry that went on to succeed.
        },
        rejected() {
          setParseStatus("expired");
          setParseToken(null);
        },
      }
    );

    return () => subscription.unsubscribe();
    // Intentionally keyed on the token and status alone: applyParsedPayload closes over the form
    // state, and adding it here would tear down and restart the subscription on every keystroke.
  }, [parseToken, parsing]);

  // Between polls the elapsed counter runs locally; each response resets it to the server's clock,
  // so a backgrounded tab still shows the true age rather than a drifting one.
  useEffect(() => {
    if (!parsing) return undefined;
    const id = setInterval(() => setParseElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(id);
  }, [parsing]);

  useEffect(() => {
    window.localStorage?.setItem(SPLIT_STORAGE_KEY, String(Math.round(formPct)));
  }, [formPct]);

  const clampPct = (value) => Math.min(MAX_PANE_PCT, Math.max(MIN_PANE_PCT, value));

  const startResize = (event) => {
    event.preventDefault();
    const container = splitRef.current?.parentElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const onMove = (moveEvent) => setFormPct(clampPct(((moveEvent.clientX - rect.left) / rect.width) * 100));
    const onUp = () => {
      setResizing(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };

    setResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const resizeByKey = (event) => {
    if (event.key === "ArrowLeft") setFormPct((prev) => clampPct(prev - 2));
    if (event.key === "ArrowRight") setFormPct((prev) => clampPct(prev + 2));
  };

  const stageLabel = PARSE_STAGES.find((s) => s.key === parseStage)?.label || "";

  useEffect(() => {
    if (!parsing) return undefined;

    const original = document.title;
    document.title = `${stageLabel || "Reading CV"}: ${original}`;
    return () => { document.title = original; };
  }, [parsing, stageLabel]);

  const handleClose = () => {
    if (parsing) {
      setConfirmCloseOpen(true);
      return;
    }
    if (parseToken) cancelExtract();
    onClose?.();
  };

  const confirmClose = () => {
    setConfirmCloseOpen(false);
    cancelExtract();
    onClose?.();
  };

  const resetAll = () => {
    setValues(initialValues);
    setResumeFile(null);
    setShowErrors(false);
    setTouched({});
    setExperiences([]);
    setTrainings([]);
    setSkills([]);
    setCertificates([]);
    setRemovedExperienceIds([]);
    setRemovedTrainingIds([]);
    setRemovedCertificateIds([]);
    setSendAutoEmail(false);
    setAutoEmailLocale("fr");
    setSelectedJobOffers([]);
    setOfferSearch("");
    setOfferResults([]);
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      setShowErrors(true);
      return;
    }

    const resolvedSource = values.source === "other" ? values.source_other.trim() : values.source.trim();
    // ai_summary is not a candidate column: it folds into recruiter_summaries under the locale it
    // was written in, leaving whatever other locales the record already carries untouched.
    const aiSummary = values.ai_summary.trim();
    const aiSummaryLocale = values.ai_summary_locale || values.preferred_locale || "en";
    const recruiterSummaries = aiSummary
      ? { ...(values.recruiter_summaries || {}), [aiSummaryLocale]: aiSummary }
      : values.recruiter_summaries;
    const payload = {
      ...values,
      ai_summary: undefined,
      ai_summary_locale: undefined,
      recruiter_summaries: recruiterSummaries,
      source_other: undefined,
      source: resolvedSource || undefined,
      desired_sector: values.desired_sector || [],
      subsectors: values.subsectors || [],
      desired_locations: splitCommaList(values.desired_locations),
      experience_year: String(values.experience_year || ""),
      preferred_locale: values.preferred_locale || undefined,
      availability: values.availability || undefined,
      candidate_status: values.candidate_status || undefined,
      highest_degree: values.highest_degree || undefined,
      experiences_attributes: [
        ...experiences.map(toExperienceAttributes),
        ...removedExperienceIds.map((id) => ({ id, _destroy: true })),
      ],
      trainings_attributes: [
        ...trainings.map(toTrainingAttributes),
        ...removedTrainingIds.map((id) => ({ id, _destroy: true })),
      ],
      certificates_attributes: [
        ...certificates.map((c) => ({ id: c.id, name: c.name, category: c.category })),
        ...removedCertificateIds.map((id) => ({ id, _destroy: true })),
      ],
      skills,
      // Lets the server record this parse against the new candidate, so the after-save parse
      // short-circuits instead of doing the same work again.
      cv_parse_token: parseStatus === "done" ? parseToken : undefined,
      cv_vault_item_id: cvVaultItemId || undefined,
      resume_source: brandedReady ? resumeSource : undefined,
      // FormData omits a key whose array is empty, and the controller reads an absent `skills` key
      // as "this request isn't about skills" (so the CV parser and bulk actions don't wipe them).
      // This flag is what tells it the form did send a list, even when that list is empty.
      skills_submitted: true,
    };

    onSubmit?.(payload, {
      reset: resetAll,
      resumeFile,
      autoEmail: {
        enabled: sendAutoEmail,
        locale: autoEmailLocale,
        job_offer_ids: selectedJobOffers.map((o) => o.id),
        custom_body_html: customEmailBody.trim() || undefined,
        password: generatedPassword || undefined,
      },
    });
  };

  return (
    <>
      {/* Stage only — the elapsed counter would be announced every second. */}
      <Box
        aria-live="polite"
        sx={{ position: "absolute", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}
      >
        {parsing ? stageLabel : ""}
      </Box>

      <Dialog
        open={open && !minimized}
        onClose={handleClose}
        fullWidth
        // Edge to edge once the CV is beside the form: two panes of a document and a thirty-field
        // form have nothing to gain from a letterboxed dialog.
        fullScreen={showViewer}
        maxWidth={showViewer ? false : "md"}
        PaperProps={{
          sx: {
            overflow: "hidden",
            borderRadius: showViewer ? 0 : "12px",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: showViewer ? 1 : 2.5,
            flexShrink: 0,
            backgroundColor: "hsl(150 38% 17%)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                backgroundColor: "hsla(80, 97%, 72%, 0.15)",
                borderRadius: "10px",
                p: showViewer ? 0.5 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isEditMode
                ? <Pencil size={showViewer ? 16 : 20} color="#fff" />
                : <UserPlus size={showViewer ? 16 : 20} color="#fff" />}
            </Box>
            <Box>
              <Typography sx={{ fontSize: showViewer ? 14 : 16, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#fff" }}>
                {isEditMode ? "Edit candidate" : "Create new candidate"}
              </Typography>
              {!showViewer && (
                <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsla(80, 97%, 72%, 0.7)" }}>
                  {isEditMode ? "Update candidate information" : "Add a new candidate to the database"}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              disableRipple
              size="small"
              onClick={() => setMinimized(true)}
              aria-label="Minimise"
              sx={{ p: 0.5, borderRadius: "6px", color: "hsla(80, 97%, 72%, 0.7)", "&:hover": { backgroundColor: "hsla(255,255,255,0.1)" } }}
            >
              <Minimize2 size={17} />
            </IconButton>
            <IconButton
              disableRipple
              size="small"
              onClick={handleClose}
              disabled={submitting}
              aria-label="Close"
              sx={{ p: 0.5, borderRadius: "6px", color: "hsla(80, 97%, 72%, 0.7)", "&:hover": { backgroundColor: "hsla(255,255,255,0.1)" } }}
            >
              <X size={18} />
            </IconButton>
          </Stack>
        </Box>

        {/* Content — form on the left, the CV itself on the right */}
        <Box
          sx={{ display: "flex", flex: 1, minHeight: 0, position: "relative" }}
          onDragEnter={handleDragEnter}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dragActive && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                pointerEvents: "none",
                backgroundColor: "hsla(150, 38%, 17%, 0.9)",
              }}
            >
              <Upload size={30} color="hsl(80 97% 72%)" />
              <Typography sx={{ fontSize: 15, color: "#fff", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                Drop the CV to attach it
              </Typography>
              <Typography sx={{ fontSize: 12, color: "hsla(80, 97%, 72%, 0.8)" }}>
                PDF or DOCX
              </Typography>
            </Box>
          )}

          {/* Positioned so the floating button anchors to the form pane rather than scrolling
              away inside DialogContent, and never drifts over the CV. */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              flex: showViewer ? `0 0 ${formPct}%` : 1,
            }}
          >
            {editingBranded && brandedReady && branded?.data ? (
              <BrandedCvEditor
                sourceUrl={brandedSourceUrl}
                data={branded.data}
                onSaved={(saved) => {
                  setBranded(saved);
                  setBrandedVersion((v) => v + 1);
                }}
                onClose={() => setEditingBranded(false)}
              />
            ) : (
              <>
                <DialogContent
                  ref={setScrollEl}
                  sx={{
                    px: 2.5,
                    // Deep enough that the last row clears the floating button.
                    pb: 9,
                    // Zero, so the sticky bar above starts flush with the scrollport rather than after
                    // the padding. MUI adds its own top padding on a first-of-type child, hence the
                    // explicit override.
                    pt: "0 !important",
                    flex: 1,
                    overflowY: "auto",
                    backgroundColor: "#fff",
                  }}
                >
                  <FormProgressBar
                    completion={completion}
                    onJump={jumpToSection}
                    activeSection={activeSection}
                  />
                  <Stack spacing={2}>
                    {/* ── 1. Basic information ── */}
                    <Box ref={(el) => { sectionRefs.current.basic = el; }} />
                    <SectionHeader icon={User} title="Basic Information" />

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <TextInput
                        label="First name"
                        required
                        error={showErrors || touched.first_name ? errors.first_name : undefined}
                        value={values.first_name}
                        onChange={set("first_name")}
                        onBlur={touch("first_name")}
                        provenance={provenance.first_name}
                        threshold={threshold}
                      />
                      <TextInput
                        label="Last name"
                        value={values.last_name}
                        onChange={set("last_name")}
                        provenance={provenance.last_name}
                        threshold={threshold}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <TextInput
                        label="Email"
                        required
                        type="email"
                        error={showErrors || touched.email ? errors.email : undefined}
                        value={values.email}
                        onChange={set("email")}
                        onBlur={touch("email")}
                        provenance={provenance.email}
                        threshold={threshold}
                      />
                      <TextInput
                        label="Phone"
                        value={values.phone}
                        onChange={set("phone")}
                        provenance={provenance.phone}
                        threshold={threshold}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <LanguageSelect
                        value={values.preferred_locale}
                        onChange={set("preferred_locale")}
                        provenance={provenance.preferred_locale}
                        threshold={threshold}
                      />
                      <SelectInput label="Source" value={values.source} onChange={setSource} options={SOURCE_OPTIONS} />
                    </Box>
                    {values.source === "other" && (
                      <TextInput label="Other source" value={values.source_other} onChange={set("source_other")} />
                    )}

                    {/* ── 2. CV ── */}
                    <Box ref={(el) => { sectionRefs.current.cv = el; }} sx={{ borderTop: "1px solid hsl(140 8% 88%)", pt: 2 }} />
                    <SectionHeader icon={FileText} title="CV" />

                    {brandedReady && (
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
                        Which CV becomes this candidate&apos;s resume?
                          </Typography>
                          <Button
                            size="small"
                            disableRipple
                            startIcon={<Pencil size={12} />}
                            onClick={() => {
                              setEditingBranded(true);
                              setResumeSource("branded");
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: 12,
                              py: 0,
                              minWidth: 0,
                              color: "hsl(150 30% 12%)",
                              "&:hover": { backgroundColor: "transparent" },
                            }}
                          >
                        Edit CV
                          </Button>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                          <ResumeChoiceCard
                            selected={resumeSource === "original"}
                            onSelect={() => setResumeSource("original")}
                            title="Original CV"
                            subtitle="Exactly as the candidate sent it"
                            filename={cvFilename}
                          />
                          <ResumeChoiceCard
                            selected={resumeSource === "branded"}
                            onSelect={() => setResumeSource("branded")}
                            title="Squarehub CV"
                            subtitle={brandedIncomplete ? incompleteMessage(branded?.data) : "Branded, ready to send to a client"}
                            filename={branded?.filename}
                            warning={brandedIncomplete}
                          />
                        </Box>
                      </Box>
                    )}

                    <Box>
                      {existingResumeUrl && !resumeFile && (
                        <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>Upload below to replace</Typography>
                          {(candidateId || cvVaultItemId) && !parsing && (
                            <ParserModelSplitButton
                              value={parserModel}
                              onChange={setParserModel}
                              onRun={(model) => startExtract(model)}
                              label={parseStatus === "done" ? "Extract again" : "Extract from current resume"}
                              icon={<Wand2 size={14} />}
                            />
                          )}
                          {(candidateId || cvVaultItemId) && parsing && (
                            <Button
                              size="small"
                              disableRipple
                              onClick={() => cancelExtract()}
                              sx={{
                                textTransform: "none",
                                fontSize: 12,
                                flexShrink: 0,
                                px: 1,
                                height: 28,
                                minHeight: 0,
                                borderRadius: "6px",
                                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                color: "hsl(0 72% 51%)",
                                border: "1px solid hsl(0 72% 70%)",
                                "&:hover": { backgroundColor: "hsl(0 80% 97%)" },
                              }}
                            >
                    Cancel CV parsing
                            </Button>
                          )}
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          component="label"
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            height: 40,
                            borderRadius: "6px",
                            border: "1px solid hsl(140 8% 88%)",
                            backgroundColor: "hsl(140 10% 95%)",
                            fontSize: 14,
                            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                            color: resumeFile ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                            boxSizing: "border-box",
                            "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.3)" },
                          }}
                        >
                          <Upload size={16} style={{ flexShrink: 0 }} />
                          {resumeFile ? resumeFile.name : (isEditMode ? "Upload new CV (PDF, DOC, DOCX)" : "Upload CV (PDF, DOC, DOCX)")}
                          <input
                            hidden
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                          />
                        </Box>
                        {resumeFile && !parsing && (
                          <ParserModelSplitButton
                            value={parserModel}
                            onChange={setParserModel}
                            onRun={(model) => startExtract(model)}
                            label={parseStatus === "done" ? "Extract again" : "Extract CV"}
                            icon={<Wand2 size={14} />}
                          />
                        )}
                        {resumeFile && parsing && (
                          <Button
                            size="small"
                            disableRipple
                            onClick={() => cancelExtract()}
                            sx={{
                              textTransform: "none",
                              fontSize: 12,
                              flexShrink: 0,
                              px: 1,
                              height: 28,
                              minHeight: 0,
                              borderRadius: "6px",
                              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                              color: "hsl(0 72% 51%)",
                              border: "1px solid hsl(0 72% 70%)",
                              "&:hover": { backgroundColor: "hsl(0 80% 97%)" },
                            }}
                          >
                  Cancel CV parsing
                          </Button>
                        )}
                        {resumeFile && (
                          <Button
                            size="small"
                            disableRipple
                            disabled={parsing}
                            onClick={() => { setResumeFile(null); cancelExtract(); }}
                            sx={{ textTransform: "none", fontSize: 12, color: "hsl(0 72% 51%)", minWidth: "auto", p: 0, flexShrink: 0, "&:hover": { backgroundColor: "transparent" } }}
                          >
                  Remove
                          </Button>
                        )}
                      </Box>

                      {resumeFile && parseStatus === "idle" && (
                        <Typography sx={{ fontSize: 12, mt: 0.75, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                Click Extract CV to fill this form from the document. Usually under two minutes,
                you can minimise the form and carry on while it runs.
                        </Typography>
                      )}

                      {parseStatus !== "idle" && (
                        <ParseStatusPanel
                          status={parseStatus}
                          stage={parseStage}
                          stageDetail={parseStageDetail}
                          expected={parseExpected}
                          elapsed={parseElapsed}
                          attempt={parseAttempt}
                          error={parseError}
                          errorKind={parseErrorKind}
                          stalled={parseStalled}
                          summary={parseSummary}
                          onRetry={() => startExtract(parserModel)}
                          onUndo={undoParse}
                          canUndo={Boolean(preParseSnapshot)}
                        />
                      )}
                    </Box>

                    {brandedSourceUrl && hasOriginalCv && !brandedReady && (
                      <Box sx={{ mb: 1.5, p: 1.5, borderRadius: "8px", border: "1px solid hsl(140 8% 88%)", backgroundColor: "hsl(140 10% 98%)" }}>
                        {branded?.status === "generating" ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <CircularProgress size={14} sx={{ color: "hsl(150 38% 17%)" }} />
                              <Typography sx={{ fontSize: 12.5, color: "hsl(150 10% 46%)" }}>
                                {brandedStageLabel(branded?.stage)}
                                {brandedStageDetail(branded?.stage, branded?.progress) ? ` · ${brandedStageDetail(branded?.stage, branded?.progress)}` : ""}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={brandedStageProgress(branded?.stage)}
                              sx={{
                                height: 2,
                                borderRadius: 2,
                                backgroundColor: "hsl(140 8% 90%)",
                                "& .MuiLinearProgress-bar": { backgroundColor: "hsl(150 38% 17%)" },
                              }}
                            />
                          </Box>
                        ) : branded?.status === "failed" ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(0 55% 40%)" }}>
                              {brandedErrorCopy({ errorKind: branded?.progress?.error_kind, error: branded?.error }).title}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
                              {brandedErrorCopy({ errorKind: branded?.progress?.error_kind, error: branded?.error }).detail}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <GenerateCvSplitButton onRun={generateBrandedCv} label="Regenerate" />
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, color: "hsl(150 10% 46%)" }}>
                          No Squarehub CV yet for this candidate
                            </Typography>
                            <GenerateCvSplitButton onRun={generateBrandedCv} />
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* ── 3. Auto email (create only) ── */}
                    {!isEditMode && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "8px",
                          backgroundColor: "hsl(140 10% 95%)",
                          border: "1px solid hsl(140 8% 88%)",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={sendAutoEmail}
                              onChange={(e) => setSendAutoEmail(e.target.checked)}
                              disableRipple
                              icon={<CircleUnchecked />}
                              checkedIcon={<CircleChecked />}
                              sx={{ p: 0, mx: "10px", "&:hover .MuiBox-root": { borderColor: "hsl(150 38% 17%)" } }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: 14, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    Send automatic email to candidate
                            </Typography>
                          }
                        />

                        {sendAutoEmail && (
                          <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <Box>
                              <Typography sx={{ ...labelSx, mb: 0.75 }}>Email language</Typography>
                              <ToggleButtonGroup
                                value={autoEmailLocale}
                                exclusive
                                onChange={(_e, next) => { if (next) setAutoEmailLocale(next); }}
                                size="small"
                                sx={{
                                  "& .MuiToggleButton-root": {
                                    textTransform: "none",
                                    borderRadius: "6px",
                                    px: 2,
                                    py: 1,
                                    fontSize: 13,
                                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                                    "&.Mui-selected": {
                                      backgroundColor: "hsl(150 38% 17%)",
                                      color: "hsl(80 97% 72%)",
                                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                      "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
                                    },
                                  },
                                }}
                              >
                                <ToggleButton disableRipple value="fr">FR</ToggleButton>
                                <ToggleButton value="nl">NL</ToggleButton>
                              </ToggleButtonGroup>
                            </Box>

                            <Box>
                              <Typography sx={labelSx}>Job offers (optional)</Typography>

                              {selectedJobOffers.length > 0 && (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                                  {selectedJobOffers.map((jo) => (
                                    <Chip
                                      key={jo.id}
                                      label={`${jo.title}${jo.company ? `, ${jo.company}` : ""}`}
                                      size="small"
                                      onDelete={() => setSelectedJobOffers((prev) => prev.filter((o) => o.id !== jo.id))}
                                      sx={{
                                        fontSize: 12,
                                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                                        backgroundColor: "hsl(150 38% 17%)",
                                        color: "hsl(80 97% 72%)",
                                        "& .MuiChip-deleteIcon": { color: "hsla(80, 97%, 72%, 0.6)", "&:hover": { color: "hsl(80 97% 72%)" } },
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              <Box sx={{ position: "relative" }}>
                                <Box
                                  component="input"
                                  placeholder="Search by job title or company..."
                                  value={offerSearch}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setOfferSearch(val);
                                    const q = val.trim();
                                    if (q.length < 3) { setOfferResults([]); return; }
                                    setSearchingOffers(true);
                                    sh.get("/internal/search/job_offers", { params: { q } })
                                      .then((res) => setOfferResults(res.data || []))
                                      .catch(() => setOfferResults([]))
                                      .finally(() => setSearchingOffers(false));
                                  }}
                                  sx={nativeInputSx}
                                />
                                <Box sx={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", display: "flex" }}>
                                  {searchingOffers ? <CircularProgress size={14} sx={{ color: "hsl(150 38% 17%)" }} /> : <Search size={14} color="hsl(150 10% 46%)" />}
                                </Box>
                              </Box>

                              {!searchingOffers && offerSearch.trim().length >= 3 && offerResults.length === 0 && (
                                <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", textAlign: "center", py: 1 }}>No offers found</Typography>
                              )}

                              {offerResults.length > 0 && (
                                <Paper elevation={0} sx={{ mt: 0.5, maxHeight: 180, overflow: "auto", border: "1px solid hsl(140 8% 88%)", borderRadius: "6px" }}>
                                  <List disablePadding dense>
                                    {offerResults
                                      .filter((jo) => !selectedJobOffers.some((s) => s.id === jo.id))
                                      .map((jo) => (
                                        <ListItemButton
                                          key={jo.id}
                                          onClick={() => {
                                            setSelectedJobOffers((prev) => [...prev, { id: jo.id, title: jo.title, company: jo.company }]);
                                            setOfferSearch("");
                                            setOfferResults([]);
                                          }}
                                          sx={{ py: 0.5, "&:hover": { backgroundColor: "hsl(140 10% 95%)" } }}
                                        >
                                          <ListItemText
                                            primary={jo.title}
                                            primaryTypographyProps={{ sx: { fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" } }}
                                            secondary={jo.company}
                                            secondaryTypographyProps={{ sx: { fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui" } }}
                                          />
                                        </ListItemButton>
                                      ))}
                                  </List>
                                </Paper>
                              )}
                            </Box>

                            {generatedPassword && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: "6px",
                                  backgroundColor: "hsl(40 100% 97%)",
                                  border: "1px solid hsl(40 80% 80%)",
                                }}
                              >
                                <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(40 60% 30%)" }}>
                        Account password:
                                </Typography>
                                <Typography
                                  component="code"
                                  sx={{
                                    fontSize: 13,
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    color: "hsl(150 38% 17%)",
                                    backgroundColor: "hsl(40 60% 92%)",
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: "4px",
                                    userSelect: "all",
                                  }}
                                >
                                  {generatedPassword}
                                </Typography>
                                <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(40 40% 50%)", ml: "auto" }}>
                        Make sure this password appears in the email below
                                </Typography>
                              </Box>
                            )}

                            <RichTextEditor
                              value={customEmailBody}
                              onChange={setCustomEmailBody}
                              label="Email content (editable)"
                              helperText="The signature is automatically added at the end of the email"
                              minHeight={300}
                            />
                          </Stack>
                        )}
                      </Paper>
                    )}

                    {/* ── 4. Location ── */}
                    <Box ref={(el) => { sectionRefs.current.location = el; }} sx={{ borderTop: "1px solid hsl(140 8% 88%)", pt: 2 }} />
                    <SectionHeader icon={MapPin} title="Location" />

                    <Box>
                      <FieldLabel label="Region" provenance={provenance.region} threshold={threshold} />
                      <TextField select fullWidth size="small" value={values.region} onChange={(e) => setRegion(e.target.value)} sx={selectSx}>
                        <MenuItem value="" sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>Select region</MenuItem>
                        {regionGroupsCreate.flatMap((group) => [
                          <ListSubheader key={group.label} disableSticky sx={{ lineHeight: "30px", fontSize: 11, fontWeight: 700, color: "hsl(150 10% 46%)", backgroundColor: "transparent" }}>
                            {group.label}
                          </ListSubheader>,
                          ...group.regions.map((r) => (
                            <MenuItem key={`${group.label}-${r}`} value={r} sx={{ fontSize: 14 }}>
                              {formatRegionMessage(r)}
                            </MenuItem>
                          )),
                        ])}
                      </TextField>
                    </Box>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5 }}>
                      <SelectInput
                        label="Country"
                        value={values.country}
                        onChange={set("country")}
                        options={COUNTRY_OPTIONS}
                        provenance={provenance.country}
                        threshold={threshold}
                      />
                      <TextInput label="City" value={values.city} onChange={set("city")} />
                      <TextInput label="Zipcode" value={values.zipcode} onChange={set("zipcode")} />
                    </Box>

                    {/* ── 5. Professional profile ── */}
                    <Box ref={(el) => { sectionRefs.current.professional = el; }} sx={{ borderTop: "1px solid hsl(140 8% 88%)", pt: 2 }} />
                    <SectionHeader icon={Briefcase} title="Professional Profile" />

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <TextInput
                        label="Current job title"
                        value={values.job_title}
                        onChange={set("job_title")}
                        provenance={provenance.job_title}
                        threshold={threshold}
                      />
                      <TextInput
                        label="Years of experience"
                        inputMode="numeric"
                        value={values.experience_year}
                        onChange={setExperienceYear}
                        provenance={provenance.experience_year}
                        threshold={threshold}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <TextInput label="Desired job title" value={values.desired_job_title} onChange={set("desired_job_title")} />
                      <SectorPicker
                        selected={values.desired_sector || []}
                        onChange={setDesiredSector}
                        provenance={provenance.desired_sector}
                        threshold={threshold}
                      />
                    </Box>
                    <SubsectorPicker
                      sectors={values.desired_sector || []}
                      selected={values.subsectors || []}
                      onChange={set("subsectors")}
                      provenance={provenance.subsectors}
                      threshold={threshold}
                    />
                    <TextInput label="Desired locations (comma separated)" value={values.desired_locations} onChange={set("desired_locations")} />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <SelectInput
                        label="Candidate status"
                        value={values.candidate_status}
                        onChange={set("candidate_status")}
                        options={candidateStatusOptions}
                        provenance={provenance.candidate_status}
                        threshold={threshold}
                      />
                      <SelectInput label="Availability" value={values.availability} onChange={set("availability")} options={AVAILABILITY_OPTIONS} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <SelectInput
                        label="Highest degree"
                        value={values.highest_degree}
                        onChange={set("highest_degree")}
                        options={HIGHEST_DEGREE_OPTIONS}
                        provenance={provenance.highest_degree}
                        threshold={threshold}
                      />
                      <TextInput label="Portfolio URL" value={values.portfolio_url} onChange={set("portfolio_url")} />
                    </Box>
                    <TextAreaInput
                      label="Presentation"
                      placeholder="The candidate's own words, copied from their CV's own profile paragraph, not written by the parser"
                      value={values.presentation}
                      onChange={set("presentation")}
                      provenance={provenance.presentation}
                      threshold={threshold}
                    />
                    <TextAreaInput
                      label="AI summary"
                      placeholder="Synthesized by the parser for recruiters: candidates never see this"
                      value={values.ai_summary}
                      onChange={set("ai_summary")}
                      provenance={provenance.ai_summary}
                      threshold={threshold}
                    />

                    {/* ── 6. Experience / Training / Skills ── */}
                    <Box ref={(el) => { sectionRefs.current.experience = el; }} />
                    <Accordion elevation={0} sx={accordionSx}>
                      <AccordionSummary expandIcon={<ChevronDown size={16} color="hsl(150 10% 46%)" />} sx={accordionSummarySx}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Briefcase size={16} color="hsl(150 38% 17%)" />
                          <Typography sx={accordionTitleSx}>
                  Experience{experiences.length > 0 ? ` (${experiences.length})` : ""}
                          </Typography>
                          {showErrors && incompleteExperiences > 0 && (
                            <Typography sx={{ ...errorTextSx, mt: 0 }}>{incompleteExperiences} incomplete</Typography>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2.5 }}>
                        <Stack spacing={2}>
                          {experiences.length === 0 && (
                            <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    No experience yet.
                            </Typography>
                          )}
                          {experiences.map((row, index) => (
                            <ExperienceRow
                              key={row._key}
                              row={row}
                              index={index}
                              onChange={updateExperience}
                              onRemove={removeExperience}
                              showErrors={showErrors}
                            />
                          ))}
                          <AddRowButton label="Add an experience" onClick={() => setExperiences((prev) => [...prev, blankExperience()])} />
                        </Stack>
                      </AccordionDetails>
                    </Accordion>

                    <Box ref={(el) => { sectionRefs.current.training = el; }} />
                    <Accordion elevation={0} sx={accordionSx}>
                      <AccordionSummary expandIcon={<ChevronDown size={16} color="hsl(150 10% 46%)" />} sx={accordionSummarySx}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <GraduationCap size={16} color="hsl(150 38% 17%)" />
                          <Typography sx={accordionTitleSx}>
                  Training{trainings.length > 0 ? ` (${trainings.length})` : ""}
                          </Typography>
                          {showErrors && incompleteTrainings > 0 && (
                            <Typography sx={{ ...errorTextSx, mt: 0 }}>{incompleteTrainings} incomplete</Typography>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2.5 }}>
                        <Stack spacing={2}>
                          {trainings.length === 0 && (
                            <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    No training yet.
                            </Typography>
                          )}
                          {trainings.map((row, index) => (
                            <TrainingRow
                              key={row._key}
                              row={row}
                              index={index}
                              onChange={updateTraining}
                              onRemove={removeTraining}
                              showErrors={showErrors}
                            />
                          ))}
                          <AddRowButton label="Add a training" onClick={() => setTrainings((prev) => [...prev, blankTraining()])} />
                        </Stack>
                      </AccordionDetails>
                    </Accordion>

                    <Box ref={(el) => { sectionRefs.current.skills = el; }} />
                    <Accordion elevation={0} sx={accordionSx}>
                      <AccordionSummary expandIcon={<ChevronDown size={16} color="hsl(150 10% 46%)" />} sx={accordionSummarySx}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Sparkles size={16} color="hsl(150 38% 17%)" />
                          <Typography sx={accordionTitleSx}>
                  Skills{skills.length > 0 ? ` (${skills.length})` : ""}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2.5 }}>
                        <Stack spacing={2}>
                          {SKILL_CATEGORIES.map(({ key, label }) => (
                            <SkillPicker
                              key={key}
                              category={key}
                              label={label}
                              selected={selectedSkillsFor(key)}
                              onChange={setSkillsFor}
                            />
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>

                    <Box ref={(el) => { sectionRefs.current.certificates = el; }} />
                    <Accordion elevation={0} sx={accordionSx}>
                      <AccordionSummary expandIcon={<ChevronDown size={16} color="hsl(150 10% 46%)" />} sx={accordionSummarySx}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Award size={16} color="hsl(150 38% 17%)" />
                          <Typography sx={accordionTitleSx}>
                  Certifications{certificates.length > 0 ? ` (${certificates.length})` : ""}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2.5 }}>
                        <CertificatePicker selected={certificates} onChange={handleCertificatesChange} />
                      </AccordionDetails>
                    </Accordion>

                    {/* ── 7. Flags ── */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        backgroundColor: "hsl(140 10% 95%)",
                        border: "1px solid hsl(140 8% 88%)",
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
                        {[
                          { key: "whitelisted", label: "Whitelisted" },
                          { key: "work_permit", label: "Work permit" },
                        ].map((item) => (
                          <FormControlLabel
                            key={item.key}
                            control={
                              <Checkbox
                                checked={!!values[item.key]}
                                onChange={updateBool(item.key)}
                                disableRipple
                                icon={<CircleUnchecked />}
                                checkedIcon={<CircleChecked />}
                                sx={{ p: 0, mx: 1, "&:hover .MuiBox-root": { borderColor: "hsl(150 38% 17%)" } }}
                              />
                            }
                            label={<Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{item.label}</Typography>}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Stack>
                </DialogContent>

                <Box sx={{ position: "absolute", right: 20, bottom: 20, zIndex: 3 }}>
                  <Tooltip
                    placement="top"
                    title={blockingReason}
                  >
                    <Box component="span">
                      <Button
                        disableRipple
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting || parsing || !canSubmit}
                        sx={{
                          textTransform: "none",
                          fontSize: 14,
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          borderRadius: "8px",
                          px: 3,
                          py: 0.75,
                          minHeight: 0,
                          height: "auto",
                          whiteSpace: "nowrap",
                          backgroundColor: "hsl(150 38% 17%)",
                          color: "hsl(80 97% 72%)",
                          boxShadow: "0 4px 14px hsla(150, 38%, 17%, 0.35)",
                          transition: "opacity 0.15s ease",
                          "&:hover": {
                            backgroundColor: "hsl(150 38% 17%)",
                            opacity: 0.9,
                            boxShadow: "0 6px 18px hsla(150, 38%, 17%, 0.45)",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.5,
                            backgroundColor: "hsl(150 38% 17%)",
                            color: "hsl(80 97% 72%)",
                            boxShadow: "0 4px 14px hsla(150, 38%, 17%, 0.2)",
                          },
                        }}
                      >
                        {parsing
                          ? `Reading the CV… ${formatSeconds(parseElapsed)}`
                          : submitting
                            ? (isEditMode ? "Saving..." : "Creating...")
                            : (isEditMode ? "Save changes" : "Create candidate")}
                      </Button>
                    </Box>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>

          {showViewer && (
            <>
              <Box
                ref={splitRef}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize the CV pane"
                aria-valuenow={Math.round(100 - formPct)}
                aria-valuemin={MIN_PANE_PCT}
                aria-valuemax={MAX_PANE_PCT}
                tabIndex={0}
                onMouseDown={startResize}
                onDoubleClick={() => setFormPct(DEFAULT_FORM_PCT)}
                onKeyDown={resizeByKey}
                sx={{
                  flex: "0 0 6px",
                  cursor: "col-resize",
                  backgroundColor: resizing ? "hsl(150 38% 17%)" : "hsl(140 8% 88%)",
                  transition: "background-color 120ms ease",
                  "&:hover": { backgroundColor: "hsl(150 38% 17%)" },
                  "&:focus-visible": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: -2 },
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0, pointerEvents: resizing ? "none" : "auto" }}>
                <CvViewer
                  fileUrl={viewerUrl}
                  isPdf={viewerIsPdf}
                  isDocx={viewerIsDocx}
                  cvText={cvText}
                  filename={showingBranded ? (branded?.filename || "Squarehub CV") : (originalName || "Current CV")}
                />
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {open && minimized && (
        <Paper
          elevation={8}
          onClick={() => setMinimized(false)}
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            zIndex: 1300,
            px: 2,
            py: 1.25,
            borderRadius: "12px",
            cursor: "pointer",
            border: "1px solid hsl(140 8% 88%)",
            backgroundColor: "hsl(150 38% 17%)",
            minWidth: 260,
            "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {parsing
              ? <CircularProgress size={15} sx={{ color: "hsl(80 97% 72%)" }} />
              : <UserPlus size={16} color="hsl(80 97% 72%)" />}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, color: "#fff", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                {isEditMode ? "Edit candidate" : "New candidate"}
              </Typography>
              <Typography noWrap sx={{ fontSize: 11, color: "hsla(80, 97%, 72%, 0.85)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                {parsing && `${PARSE_STAGES.find((s) => s.key === parseStage)?.label || "Working"}: ${formatSeconds(parseElapsed)}`}
                {parseStatus === "done" && "CV read, click to review"}
                {parseStatus === "failed" && "CV could not be read"}
                {parseStatus === "expired" && "Extraction expired"}
                {parseStatus === "idle" && "Click to reopen"}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <ConfirmDialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        onConfirm={confirmClose}
        title="Cancel CV extraction?"
        description="The CV is still being read. Closing now cancels it and discards everything you have entered on this form."
        cancelLabel="Keep editing"
        confirmLabel="Cancel and close"
        destructive
      />

      <CvUpdateReviewModal
        open={Boolean(pendingReview)}
        scalarConflicts={pendingReview?.scalarConflicts || []}
        experienceConflicts={pendingReview?.experienceConflicts || []}
        additionsCount={pendingReview?.additionsCount || 0}
        onApply={resolveConflicts}
      />
    </>
  );
};

export default CreateCandidateModal;
